//-------------------------------------------------------------------------------------
//
//	FoldableMaker
//
//		Create plans for folding boxes
//
//-------------------------------------------------------------------------------------

// To Do:
// -----
//	Common
//		Add symmetric tab/slot
//  	Review usage: GenerateSVG.Start(exportW, exportH, BasicUnits.MILLIMETERS);
//		Review usage: GenerateSVG.SetInvertVertical(false);
//		Show errors: var errorColor = "red";
//	
//	Box
//		Fix position of pyramid box
//
//	Shape
//		Add square, pentagon, triangle, golden rhombus
//
//	Debug render?
//		review: lineWidth = 5;
//		review: edgeCutColor     = "rgba(0, 255, 0, 0.20)";
//		review: slotCutColor     = "rgba(255,0, 255, 0.20)";
//		review: insideScoreColor = "rgba(0, 0, 0, 0.20)";
//		review: edgeScoreColor   = "rgba(0, 0, 255, 0.20)";
//

//-------------------------------------------------------------------------------------
//	Imports
//-------------------------------------------------------------------------------------
import { GfxApp } from "../../GfxContainer/src/GfxApp.js";
import { GfxDrawing, GfxPath } from "../../GfxContainer/src/GfxDrawing.js";
import { MathUtil } from "../../VectorUtilsJS/src/VectorUtilLib.js";
import { PolygonList } from "../../VectorUtilsJS/src/VectorUtilLib.js";

//-------------------------------------------------------------------------------------
//	Constants
//-------------------------------------------------------------------------------------
// Floating point error allowance
let epsilon = 0.0001;

//-------------------------------------------------------------------------------------
//	Constructor
//-------------------------------------------------------------------------------------
function FoldableDesignerGfxApp(appName, appId, appDefaultSettings, appControlGroups)
{
	// Merge default settings from subclass
	let defaultSettings = Object.assign({}, FoldableDesignerGfxApp.defaultSettings);
	Object.assign(defaultSettings, appDefaultSettings);
	
	// appConfig is the same for all 'foldable' classes
	// (maybe allow a different filename later)
	let appConfig = {origin: "topleft", ypositive:"down", filename:"foldableDesign", initialZoom: "fit" };
	
	let appInfo = {
		appId: appId,
		name: appName, 
		defaultSettings: defaultSettings, 
		controlGroups: appControlGroups,
		appConfig: appConfig
	};
	
	GfxApp.call(this, appInfo);

	// appControlGroups is fully specified in the subclass.
	//GfxApp.call(this, appName, defaultSettings, appControlGroups, appConfig);
	
	this.EdgeList = [];
}

FoldableDesignerGfxApp.prototype = Object.create(GfxApp.prototype);

Object.defineProperty(FoldableDesignerGfxApp.prototype, 'constructor', {
	value: FoldableDesignerGfxApp,
	enumerable: false,
	write: true });

//-------------------------------------------------------------------------------------
//	Constants
//-------------------------------------------------------------------------------------
FoldableDesignerGfxApp.EdgeType = Object.freeze({
		SCORE:	0,
		TAB:	1,
		SLOT:	2,
		CUT:	3,
		FLAP:	4
	});

FoldableDesignerGfxApp.EdgeRender = Object.freeze({
		OMIT:	      0,	// Do not draw anything for this edge
		DEFAULT:      1,	// Draw the default
		LINE:         2,	// Draw a straight line (which is also the default for SCORE and CUT
		BEVEL:        3,
		CURVE:	      4,
		
		DOTTED:       5,	// Score
		DASHED:       6,
		
		ROUNDED:      7,	// Tab only

		FLAP:         8,	// Flap only (NOTE: Don't confuse the EdgeType.FLAP with the EdgeRender.FLAP)
		SYMMETRICAL:  9
	});


//-------------------------------------------------------------------------------------
//	Default settings (for all foldable plans)
//-------------------------------------------------------------------------------------
FoldableDesignerGfxApp.defaultSettings =
{
	fillColor: "#f8f8ff",

	tabRender:   FoldableDesignerGfxApp.EdgeRender.CURVE,
	tabInset: 9,
	tabDepth: 5,
	tabValue: 3,   // For Rounded, Bevel, and Oval
	tabUndercut: 0,
	
	slotRender:  FoldableDesignerGfxApp.EdgeRender.BEVEL,
	slotInset: 4,
	slotDepth: 5,
	slotWidth: 0,  // Set to non-zero to cut a rect for the slot instead of a line
	slotValue: 3,  // For Rounded and Bevel
	slotClearance: 0,	// Clearance between tab and slot
	
	flapInset: 1,
	flapDepth: 20,
	flapValue: 2,
	
	scoreRender: FoldableDesignerGfxApp.EdgeRender.DEFAULT,
	scoreDotSpacing: 2,
	scoreDotRadius: 0.1,
	
	separatePaths: false
}


//-------------------------------------------------------------------------------------
//	Control Menus
//-------------------------------------------------------------------------------------
FoldableDesignerGfxApp.tabRenderSelection = 
	[
	//	{ label: "Omit",	value: FoldableDesignerGfxApp.EdgeRender.OMIT    },
		{ label: "Square",	value: FoldableDesignerGfxApp.EdgeRender.DEFAULT },
		{ label: "Line",	value: FoldableDesignerGfxApp.EdgeRender.LINE    },
		{ label: "Bevel",	value: FoldableDesignerGfxApp.EdgeRender.BEVEL   },
		{ label: "Curve",	value: FoldableDesignerGfxApp.EdgeRender.CURVE   },
		{ label: "Rounded",	value: FoldableDesignerGfxApp.EdgeRender.ROUNDED },
		//{ label: "Symmetrical",	value: FoldableDesignerGfxApp.EdgeRender.SYMMETRICAL }
	];
	
FoldableDesignerGfxApp.slotRenderSelection = 
	[
	//	{ label: "Omit",	value: FoldableDesignerGfxApp.EdgeRender.OMIT    },
		{ label: "Square",	value: FoldableDesignerGfxApp.EdgeRender.DEFAULT },
		{ label: "Line",	value: FoldableDesignerGfxApp.EdgeRender.LINE    },
		{ label: "Bevel",	value: FoldableDesignerGfxApp.EdgeRender.BEVEL   },
		{ label: "Curve",	value: FoldableDesignerGfxApp.EdgeRender.CURVE   },
		//{ label: "Symmetrical",	value: FoldableDesignerGfxApp.EdgeRender.SYMMETRICAL }
	];
	
FoldableDesignerGfxApp.scoreRenderSelection = 
	[
		{ label: "Line",	value: FoldableDesignerGfxApp.EdgeRender.DEFAULT },
		{ label: "Dotted",	value: FoldableDesignerGfxApp.EdgeRender.DOTTED  },
		{ label: "Dashes",	value: FoldableDesignerGfxApp.EdgeRender.DASHED  }
	];
	
//-------------------------------------------------------------------------------------
//	Controls
//-------------------------------------------------------------------------------------
FoldableDesignerGfxApp.tabControls =
	[
		{ label: "Inset",			type: "number",		prop: "tabInset",		min:0, max: 20, step:0.1, entry:"yes"	},
		{ label: "Depth",			type: "number",		prop: "tabDepth",		min:0, max: 20, step:0.1, entry:"yes"	},
		{ label: "Undercut",		type: "number",		prop: "tabUndercut",	min:0, max: 5,  step:0.1, entry:"yes"	},
		{ label: "Shape", 			type: "selection",	prop: "tabRender",		options: FoldableDesignerGfxApp.tabRenderSelection	},
		{ label: "Amount",			type: "number",		prop: "tabValue",		min:0, max: 20, step:0.1, entry:"yes"	},
	];

FoldableDesignerGfxApp.slotControls =
	[
		{ label: "Inset",			type: "number",		prop: "slotInset",		min:0, max: 20, step:0.1, entry:"yes"	},
		{ label: "Depth",			type: "number",		prop: "slotDepth",		min:0, max: 20, step:0.1, entry:"yes"	},
		{ label: "Clearance",		type: "number",		prop: "slotClearance",	min:0, max: 5,  step:0.1, entry:"yes"	},
		{ label: "Shape", 			type: "selection",	prop: "slotRender",		options: FoldableDesignerGfxApp.slotRenderSelection	},
		{ label: "Amount",			type: "number",		prop: "slotValue",		min:0, max: 20, step:0.1, entry:"yes"	},
		{ label: "Width",			type: "number",		prop: "slotWidth",		min:0, max: 3,  step:0.1, entry:"yes"	},
	];

FoldableDesignerGfxApp.flapControls =
	[
		{ label: "Inset",			type: "number",		prop: "flapInset",		min:0, max: 20, step:0.1, entry:"yes"	},
		{ label: "Depth",			type: "number",		prop: "flapDepth",		min:0, max: 100, step:0.1, entry:"yes"	},
		{ label: "Amount",			type: "number",		prop: "flapValue",		min:0, max: 20, step:0.1, entry:"yes"	},
	];

FoldableDesignerGfxApp.scoreControls =
	[
		{ label: "Style", 			type: "selection",	prop: "scoreRender",	options: FoldableDesignerGfxApp.scoreRenderSelection	},
		{ label: "Spacing",			type: "number",		prop: "scoreDotSpacing",min:1,   max: 20,  step:0.1, entry:"yes"	},
		{ label: "Radius",			type: "number",		prop: "scoreDotRadius", min:0.1, max: 5,   step:0.1, entry:"yes"	},
	];
	
FoldableDesignerGfxApp.moreControls =
	[
		{ label: "Separate Paths",	type: "boolean",	prop: "separatePaths"	}
	];



//-------------------------------------------------------------------------------------
//	Short constants
//-------------------------------------------------------------------------------------
let TAB   = FoldableDesignerGfxApp.EdgeType.TAB;
let SLOT  = FoldableDesignerGfxApp.EdgeType.SLOT;
let SCORE = FoldableDesignerGfxApp.EdgeType.SCORE;
let CUT   = FoldableDesignerGfxApp.EdgeType.CUT;
let FLAP  = FoldableDesignerGfxApp.EdgeType.FLAP;
let MOVE  = -1;


//-------------------------------------------------------------------------------------
//	Generate Path Sets From EdgeList
//-------------------------------------------------------------------------------------
FoldableDesignerGfxApp.prototype.GenerateGfxDrawingFromEdgeList = function(edges, settings)
{
	var cutPath          = new GfxPath();
	var slotCutPath      = new GfxPath();
	var insideScorePath  = new GfxPath();
	var edgeScorePath    = new GfxPath();
	var errorPath        = new GfxPath();
	
	var	cutPathStarted = false;
	
	var dotSpacing = (settings.scoreDotSpacing > 0.1) ? settings.scoreDotSpacing : 0.1; 
	var dotRadius = (settings.scoreDotRadius > 0.1) ? settings.scoreDotRadius : 0.1;
	
	// Repackage the settings into an array indexed by edge type
	var stgs = []

	stgs[FoldableDesignerGfxApp.EdgeType.SLOT] = { 
		render: settings.slotRender, depth: settings.slotDepth, 
		inset: settings.slotInset,   value: settings.slotValue,  clearance: settings.slotClearance, width: settings.slotWidth };
		
	stgs[FoldableDesignerGfxApp.EdgeType.TAB]  = { 
		render: settings.tabRender,  depth: settings.tabDepth,  
		inset: settings.tabInset,    value: settings.tabValue,   undercut: settings.tabUndercut };
		
	stgs[FoldableDesignerGfxApp.EdgeType.FLAP]  = { 
		render: FoldableDesignerGfxApp.EdgeRender.FLAP,  depth: settings.flapDepth,  
		inset: settings.flapInset,    value: settings.flapValue   };
		
	stgs[FoldableDesignerGfxApp.EdgeType.CUT]  = { render: FoldableDesignerGfxApp.EdgeRender.LINE};
	
	stgs[FoldableDesignerGfxApp.EdgeType.SCORE]= { render: FoldableDesignerGfxApp.EdgeRender.OMIT};
	
	stgs[FoldableDesignerGfxApp.EdgeType.NONE] = {};
	
	// If the "value" (amount of bevel/curve/rounded) is zero, then use default (square). This eliminates
	// zero-sized graphics at the corners
	if (settings.slotValue == 0 && 
	    (settings.slotRender == FoldableDesignerGfxApp.EdgeRender.BEVEL ||
	     settings.slotRender == FoldableDesignerGfxApp.EdgeRender.CURVE))
	     stgs[FoldableDesignerGfxApp.EdgeType.SLOT].render = FoldableDesignerGfxApp.EdgeRender.DEFAULT;
	     
	if (settings.tabValue == 0 && 
	    (settings.tabRender == FoldableDesignerGfxApp.EdgeRender.BEVEL ||
	     settings.tabRender == FoldableDesignerGfxApp.EdgeRender.CURVE ||
	     settings.tabRender == FoldableDesignerGfxApp.EdgeRender.ROUNDED))
	     stgs[FoldableDesignerGfxApp.EdgeType.TAB].render = FoldableDesignerGfxApp.EdgeRender.DEFAULT;
	
	// If the depth is zero, then use a line. For a slot, this removes the slot cut. For both
	// a tab and a slot, this removes zero-length lines
	if (settings.slotDepth == 0 && settings.slotRender != FoldableDesignerGfxApp.EdgeRender.OMIT)
	     stgs[FoldableDesignerGfxApp.EdgeType.SLOT].render = FoldableDesignerGfxApp.EdgeRender.LINE;

	if (settings.tabDepth == 0 && settings.tabRender != FoldableDesignerGfxApp.EdgeRender.OMIT)
	     stgs[FoldableDesignerGfxApp.EdgeType.TAB].render = FoldableDesignerGfxApp.EdgeRender.LINE;

	if (settings.flapDepth == 0 && settings.flapRender != FoldableDesignerGfxApp.EdgeRender.OMIT)
	     stgs[FoldableDesignerGfxApp.EdgeType.FLAP].render = FoldableDesignerGfxApp.EdgeRender.LINE;

	// Prevent out-of-range inset values during generation
	var edgeTypeList = [FoldableDesignerGfxApp.EdgeType.SLOT, FoldableDesignerGfxApp.EdgeType.TAB];
	for (var j = 0; j < edgeTypeList.length; j++ )
	{
		var e = edgeTypeList[j];
		var s = stgs[e];
		
		var minSlotOrTab = 0;
		
		if (s.render == FoldableDesignerGfxApp.EdgeRender.BEVEL   ||
			s.render == FoldableDesignerGfxApp.EdgeRender.CURVE   ||
			s.render == FoldableDesignerGfxApp.EdgeRender.ROUNDED)
		{
			let v = s.value;
			if (v >= s.depth)
				v = s.depth;
			minSlotOrTab =  2 * v;
		}
		
		//if (s.inset > (settings.size - minSlotOrTab)/2)
		//	s.inset = (settings.size - minSlotOrTab)/2;
	}
	
	var pt;
	
	for (var i = 0; i < edges.length; i++)
	{
		// Get the info for the edge
		var e = edges[i];
		var edgeType = e.edgeType

		//
		let edgeSubdivideCount = (e.config != undefined && e.config.subdivide != undefined) ? e.config.subdivide : 1;
		
		// Calculate unit vectors and length
		var uv = MathUtil.CalcUnitVector(e.ptA, e.ptB);
		var up = {x:uv.y, y:-uv.x};
		var len = MathUtil.DistanceBetween(e.ptA, e.ptB);
		
		// For subdivided edges, adjust the length
		len /= edgeSubdivideCount;
		
		// Iterate to render the subdivided edges
		for (var edgeSubdivide = 0; edgeSubdivide < edgeSubdivideCount; edgeSubdivide++)
		{
			var ptA = PointVectorAdd(e.ptA, uv, len * edgeSubdivide);
			var ptB = PointVectorAdd(ptA, uv, len);

			// Is it a cut line?
			var isCut = (edgeType == TAB || edgeType == SLOT || edgeType == CUT || edgeType == FLAP);

			// Get the settings for the edge
			let s = stgs[edgeType];
		
			// Is it too short for tab/slot/flap?
			if (isCut && s.inset * 2 >= len)
			{
				edgeType = CUT;
				s = stgs[edgeType];
			}
		
			// Prevent errors from referencing undefined and, instead, add
			// errors to the error path
			if (s == undefined)
				s = {};

			var v = s.value;
		
					
			// Draw the continuous cut edge according to the type 
			// (that we looked up in the repackaged settings array)
			if (isCut && !cutPathStarted)
			{
				cutPath.MoveTo(ptA);
				cutPathStarted = true;
			}
			
			if (s.render == FoldableDesignerGfxApp.EdgeRender.DEFAULT)
			{
				var undercut = (s.undercut != undefined) ? s.undercut : 0;
			
				// Default is a rectangular tab with square corners
				//cutPath.MoveTo(ptA);
			
				if (undercut > 0)
				{
					pt = PointVectorAdd(ptA, uv, s.inset + undercut);
					cutPath.LineTo(pt);
				}

				pt = PointVectorAdd(ptA, uv, s.inset);
				cutPath.LineTo(pt);
				pt = PointVectorAdd(pt, up, s.depth);
				cutPath.LineTo(pt);
				pt = PointVectorAdd(pt, uv, len - 2 * s.inset);
				cutPath.LineTo(pt);
				pt = PointVectorAdd(pt, up, -s.depth);
				cutPath.LineTo(pt);
				if (undercut > 0)
				{
					pt = PointVectorAdd(pt, uv, -undercut);
					cutPath.LineTo(pt);
				}
				cutPath.LineTo(ptB);
			}
			else if (s.render == FoldableDesignerGfxApp.EdgeRender.LINE)
			{
				//cutPath.MoveTo(ptA);
				cutPath.LineTo(ptB);
			}
			else if (s.render == FoldableDesignerGfxApp.EdgeRender.BEVEL)
			{
				var undercut = (s.undercut != undefined) ? s.undercut : 0;

				// Determine if the entire corner is cut off
				var bevelOnly = false;
				if (v >= s.depth)
				{
					v = s.depth;
					bevelOnly = true;
				}
			
				// If the inset is too large, then limit the bevel
				var drawTop = true;
				if (2 * v + 2 * s.inset >= len)
				{
					v = len/2 - s.inset;
					drawTop = false;
				}

				// Initialize pt to be the starting point for the edge.
				// This is only used if the inset is zero and there is no undercut
				pt = ptA;
			
				// Start at the beginning of the edge
				//cutPath.MoveTo(ptA);
			
				// Draw the first piece of the line along the edge if we have either
				// an inset value or an undercut value
				if (s.inset > 0 || undercut > 0)
				{
					pt = PointVectorAdd(ptA, uv, s.inset + undercut);
					cutPath.LineTo(pt);
				}
			
				if (undercut > 0)
				{
					pt = PointVectorAdd(pt, uv, -undercut);
					cutPath.LineTo(pt);
				}

				// If not a bevelOnly, then add the line connecting the edge to the bevel
				if (!bevelOnly)
				{
					pt = PointVectorAdd(pt, up, s.depth - v);
					cutPath.LineTo(pt);
				}
				// Draw the bevel
				pt = PointVectorAdd(pt, up, v);
				pt = PointVectorAdd(pt, uv, v);
				cutPath.LineTo(pt);
				if (drawTop)
				{
					pt = PointVectorAdd(pt, uv, len - 2 * s.inset - 2 * v);
					cutPath.LineTo(pt);
				}
				pt = PointVectorAdd(pt, up, -v);
				pt = PointVectorAdd(pt, uv, v);
				cutPath.LineTo(pt);
				if (!bevelOnly)
				{
					pt = PointVectorAdd(pt, up, -s.depth + v);
					cutPath.LineTo(pt);
				}
			
				if (undercut > 0)
				{
					pt = PointVectorAdd(ptB, uv, -(s.inset + undercut));
					cutPath.LineTo(pt);
				}
			
				if (s.inset > 0 || undercut > 0)
					cutPath.LineTo(ptB);
			}
			else if (s.render == FoldableDesignerGfxApp.EdgeRender.FLAP)
			{
				// Initialize pt to be the starting point for the edge.
				// This is only used if the inset is zero and there is no undercut
				pt = ptA;
			
				// Start at the beginning of the edge
				//cutPath.MoveTo(ptA);
			
				// Draw the first piece of the line along the edge if we have
				// an inset value
				if (s.inset > 0)
				{
					pt = PointVectorAdd(ptA, uv, s.inset);
					cutPath.LineTo(pt);
				}

				// Edge of flap
				pt = PointVectorAdd(pt, up, s.depth);
				pt = PointVectorAdd(pt, uv, v);
				cutPath.LineTo(pt);

				pt = PointVectorAdd(pt, uv, len - 2 * s.inset - 2 * v);
				cutPath.LineTo(pt);
			
				pt = PointVectorAdd(pt, up, -s.depth);
				pt = PointVectorAdd(pt, uv, v);
				cutPath.LineTo(pt);
			
				if (s.inset > 0)
					cutPath.LineTo(ptB);
			}
			else if (s.render == FoldableDesignerGfxApp.EdgeRender.CURVE)
			{
				// Initialize pt to be the starting point for the edge.
				// This is only used if the inset is zero and there is no undercut
				pt = ptA;
			
				let q = 3.14159/2;
				var sa = 2*q

				var undercut = (s.undercut != undefined) ? s.undercut : 0;
			
				var curveOnly = false;
				if (v >= s.depth)
				{
					v = s.depth;
					curveOnly = true;
				}
			
				// If the inset is too large, then limit the curve
				var drawTop = true;
				if (2 * v + 2 * s.inset >= len)
				{
					v = len/2 - s.inset;
					drawTop = false;
				}

				sa = MathUtil.CalcAngle(ptA, ptB);
				sa += 2*q;

				//cutPath.MoveTo(ptA);
				if (s.inset > 0 || undercut > 0)
				{			
					pt = PointVectorAdd(ptA, uv, s.inset + undercut);
					cutPath.LineTo(pt);
				}
			
				if (undercut > 0)
				{
					pt = PointVectorAdd(pt, uv, -undercut);
					cutPath.LineTo(pt);
				}
			
				if (!curveOnly)
				{
					pt = PointVectorAdd(pt, up, s.depth - v);
					cutPath.LineTo(pt);
				}
				pt = PointVectorAdd(pt, uv, v);
				cutPath.Arc(pt, v, sa, sa + q, 0);
				pt = PointVectorAdd(pt, up, v);
				if (drawTop)
				{
					pt = PointVectorAdd(pt, uv, len - 2 * s.inset - 2 * v);
					cutPath.LineTo(pt);
				}
				pt = PointVectorAdd(pt, up, -v);
				cutPath.Arc(pt, v, sa + q, sa + 2*q, 0);
				pt = PointVectorAdd(pt, uv, v);
				if (!curveOnly)
				{
					pt = PointVectorAdd(pt, up, -s.depth + v);
					cutPath.LineTo(pt);
				}

				if (undercut > 0)
				{
					pt = PointVectorAdd(ptB, uv, -(s.inset + undercut));
					cutPath.LineTo(pt);
				}
			
				if (s.inset > 0 || undercut > 0)
					cutPath.LineTo(ptB);
			}
			else if (s.render == FoldableDesignerGfxApp.EdgeRender.ROUNDED)
			{
				let q = 3.14159/2;
				var sa = 2*q

				var undercut = (s.undercut != undefined) ? s.undercut : 0;
			
				var curveOnly = false;
				if (v >= s.depth/2)
				{
					v = s.depth/2;
					curveOnly = true;
				}
			
				// If the inset is too large, then limit the curve
				var drawTop = true;
				if (2 * v + 2 * s.inset >= len)
				{
					v = len/2 - s.inset;
					drawTop = false;
				}

				sa = MathUtil.CalcAngle(ptA, ptB);

				//cutPath.MoveTo(ptA);
				pt = PointVectorAdd(ptA, uv, s.inset + v + undercut);
				cutPath.LineTo(pt);
				if (undercut > 0)
				{
					pt = PointVectorAdd(pt, uv, -undercut);
					cutPath.LineTo(pt);
				}
			
				pt = PointVectorAdd(pt, up, v);
				cutPath.Arc(pt, v, sa + q , sa + 2 * q, 0);
				pt = PointVectorAdd(pt, uv, -v);

				if (!curveOnly)
				{
					pt = PointVectorAdd(pt, up, s.depth - 2*v);
					cutPath.LineTo(pt);
				}
				pt = PointVectorAdd(pt, uv, v);
				cutPath.Arc(pt, v, sa + 2 * q, sa + 3 * q, 0);
				pt = PointVectorAdd(pt, up, v);

				if (drawTop)
				{
					pt = PointVectorAdd(pt, uv, len - 2 * s.inset - 2 * v);
					cutPath.LineTo(pt);
				}
				pt = PointVectorAdd(pt, up, -v);
				cutPath.Arc(pt, v, sa + 3 * q, sa, 0);
				pt = PointVectorAdd(pt, uv, v);

				if (!curveOnly)
				{
					pt = PointVectorAdd(pt, up, -s.depth + 2*v);
					cutPath.LineTo(pt);
				}
			
				pt = PointVectorAdd(pt, uv, -v);
				cutPath.Arc(pt, v, sa, sa + q, 0);
			
				pt = PointVectorAdd(pt, up, -v);
				if (undercut > 0)
				{
					pt = PointVectorAdd(pt, uv, -undercut);
					cutPath.LineTo(pt);
					pt = PointVectorAdd(pt, uv, undercut);
					cutPath.LineTo(pt);
				}
				cutPath.LineTo(ptB);
			}
			else if (s.render == FoldableDesignerGfxApp.EdgeRender.SYMMETRICAL)
			{
				//    ------       ------    
				//   /      \     /      \    
				//   --    --     --    --    
				//    |    |       |    |     
				//-----    ---------    ------
				//
				let q = 3.14159/2;
				var sa = 2*q
			
				let symCount = 4;
				let offset = 1.0;
				let overlap = 2.0;

				var undercut = (s.undercut != undefined) ? s.undercut : 0;
			
				var symLen = (len - 2 * s.inset)/symCount;

				v = 0;			
			
				//cutPath.MoveTo(ptA);
				pt = PointVectorAdd(ptA, uv, s.inset);// + v + undercut);
				cutPath.LineTo(pt);
			
				for (var kk = 0; kk < symCount/2; kk++)
				{
					//if (undercut > 0)
					//{
					//	pt = PointVectorAdd(pt, uv, -undercut);
					//	cutPath.LineTo(pt);
					//}
			
					pt = PointVectorAdd(pt, up, s.depth);
					cutPath.LineTo(pt);
				
					pt = PointVectorAdd(pt, uv, symLen);
					cutPath.LineTo(pt);

					pt = PointVectorAdd(pt, up, -s.depth);
					cutPath.LineTo(pt);
				
					pt = PointVectorAdd(pt, uv, symLen);
					cutPath.LineTo(pt);

					//if (undercut > 0)
					//{
					//	pt = PointVectorAdd(pt, uv, -undercut);
					//	cutPath.LineTo(pt);
					//	pt = PointVectorAdd(pt, uv, undercut);
					//	cutPath.LineTo(pt);
					//}
				}
			
				cutPath.LineTo(ptB);
			}
			else if (s.render == FoldableDesignerGfxApp.EdgeRender.OMIT)
			{
			}
			else // Error
			{
				errorPath.MoveTo(ptA);
				errorPath.LineTo(ptB);
			}
		
			// If the cut path is closed
			if (e.close && isCut && cutPathStarted && (edgeSubdivide == edgeSubdivideCount - 1))
			{
				cutPath.Close();
				cutPathStarted = false;
			}

			// Add tab score lines
			if (edgeType == FoldableDesignerGfxApp.EdgeType.TAB)
			{
				var scorePtA;
				var scorePtB;
				if (s.render == FoldableDesignerGfxApp.EdgeRender.DEFAULT ||
					s.render == FoldableDesignerGfxApp.EdgeRender.BEVEL   ||
					s.render == FoldableDesignerGfxApp.EdgeRender.CURVE   ||
					s.render == FoldableDesignerGfxApp.EdgeRender.ROUNDED)
				{
					// Tab score
					let undercut = stgs[FoldableDesignerGfxApp.EdgeType.TAB].undercut;
					var scoreInset = s.inset + undercut;
				
					if (s.render == FoldableDesignerGfxApp.EdgeRender.ROUNDED)
						scoreInset += v;

					scorePtA = PointVectorAdd(ptA, uv,  scoreInset);
					scorePtB = PointVectorAdd(ptB, uv, -scoreInset);
					this.AddScoreLine(edgeScorePath, scorePtA, scorePtB, settings.scoreRender, dotSpacing, dotRadius);
				}
			}
		
			// Add slot cut lines and score lines
			else if (edgeType == FoldableDesignerGfxApp.EdgeType.SLOT)
			{
				var clearance = stgs[FoldableDesignerGfxApp.EdgeType.SLOT].clearance;
				var tabInset  = stgs[FoldableDesignerGfxApp.EdgeType.TAB].inset;
				var slotInset = stgs[FoldableDesignerGfxApp.EdgeType.SLOT].inset
			
				let slotCutLength = len - 2 * (tabInset - clearance);

				if (s.render == FoldableDesignerGfxApp.EdgeRender.DEFAULT ||
					s.render == FoldableDesignerGfxApp.EdgeRender.BEVEL   ||
					s.render == FoldableDesignerGfxApp.EdgeRender.CURVE)
				{
					// Cut either a line or a rectangle for the slot
					if (s.width == 0)
					{
						pt = PointVectorAdd(ptA, uv, tabInset - clearance);
						slotCutPath.MoveTo(pt);
						pt = PointVectorAdd(pt, uv, len - 2 * (tabInset - clearance));
						slotCutPath.LineTo(pt);
					}
					else
					{
						pt = PointVectorAdd(ptA, uv, tabInset - clearance);
						slotCutPath.MoveTo(pt);
						pt = PointVectorAdd(pt, uv, slotCutLength);
						slotCutPath.LineTo(pt);
						pt = PointVectorAdd(pt, up, s.width);
						slotCutPath.LineTo(pt);
						pt = PointVectorAdd(pt, uv, -slotCutLength);
						slotCutPath.LineTo(pt);
						slotCutPath.Close();
					}

					// Score lines on both sides of the slot
					var scorePtA = PointVectorAdd(ptA, uv, slotInset);
					var scorePtB = PointVectorAdd(ptA, uv, tabInset - clearance);
					this.AddScoreLine(edgeScorePath, scorePtA, scorePtB, settings.scoreRender, dotSpacing, dotRadius);
			
					var scorePtA = PointVectorAdd(ptA, uv, len - tabInset + clearance);
					var scorePtB = PointVectorAdd(ptA, uv, len - slotInset);
					this.AddScoreLine(edgeScorePath, scorePtA, scorePtB, settings.scoreRender, dotSpacing, dotRadius);
				}
			}

			// Add tab score lines
			else if (edgeType == FoldableDesignerGfxApp.EdgeType.FLAP)
			{
				var scorePtA;
				var scorePtB;
				if (1)
				{
					// Tab score
					var scoreInset = s.inset;
				
					scorePtA = PointVectorAdd(ptA, uv,  scoreInset);
					scorePtB = PointVectorAdd(ptB, uv, -scoreInset);
					this.AddScoreLine(edgeScorePath, scorePtA, scorePtB, settings.scoreRender, dotSpacing, dotRadius);
				}
			}
			// Add inside score lines
			else if (edgeType == FoldableDesignerGfxApp.EdgeType.SCORE)
			{
				this.AddScoreLine(insideScorePath, ptA, ptB, settings.scoreRender, dotSpacing, dotRadius);
			}
		
		} // Edge Subdivide
	}
	
	// Close the cutPath
	// NOTE: This will have to be changed when separate sections are supported
	if (cutPathStarted)
	{
		cutPath.Close();
		cutPathStarted = false;
	}
	
	
	// Assemble the gfxDrawing
	var gfxDrawing = new GfxDrawing();
	
	
	var lineWidth = 0.25;
	var cutColor = "#000000";
	var scoreColorDefault = "#CCCCCC";
	var usesScore = (this.settings.scoreRender == FoldableDesignerGfxApp.EdgeRender.DEFAULT);
	var scoreColor = usesScore ? scoreColorDefault : cutColor;
	var fillColor = this.settings.fillColor;
	
	// Setting a fill color for the cutPath for Cricut to use, otherwise it uses the line color.
	// Also note that the cutPath is first because it is now filled. This also puts it in the back for Cricut.
	
	
	if (this.settings.separatePaths)
	{
		// Add each part as a separate path in the drawing. For SVG, these will become separate path elements.
		gfxDrawing.AddPath(cutPath,         { strokePath:true, strokeColor:cutColor,   strokeWidth:lineWidth, fillPath:true, fillColor:fillColor} );
		gfxDrawing.AddPath(slotCutPath,     { strokePath:true, strokeColor:cutColor,   strokeWidth:lineWidth} );
		gfxDrawing.AddPath(edgeScorePath,   { strokePath:true, strokeColor:scoreColor, strokeWidth:lineWidth} );
		gfxDrawing.AddPath(insideScorePath, { strokePath:true, strokeColor:scoreColor, strokeWidth:lineWidth} );
	}
	else
	{
		// Combine the cut parts into one path and the score parts into another path. The SVG file will now have only two paths.
		// This helps with Cricut, since only one item has to be changed into a "score" type.
		cutPath.Append(slotCutPath);
		insideScorePath.Append(edgeScorePath);

		// Is we are not using score, then combine the score path with the cut path so everything is in a single path.
		if (usesScore)
		{
			gfxDrawing.AddPath(cutPath,         { strokePath:true, strokeColor:cutColor,   strokeWidth:lineWidth, fillPath:true, fillColor:fillColor} );
			gfxDrawing.AddPath(insideScorePath, { strokePath:true, strokeColor:scoreColor, strokeWidth:lineWidth} );
		}
		else
		{
			cutPath.Append(insideScorePath);
			gfxDrawing.AddPath(cutPath,         { strokePath:true, strokeColor:cutColor,   strokeWidth:lineWidth, fillPath:true, fillColor:fillColor} );
		}
	}

	//gfxDrawing.AddPath(errorPath,       { strokePath:true, strokeColor:"#FF0000",  strokeWidth:lineWidth} );

	return gfxDrawing;
}

//-------------------------------------------------------------------------------------
//	Add Score Line
//-------------------------------------------------------------------------------------
FoldableDesignerGfxApp.prototype.AddScoreLine = function(gfxPath, ptA, ptB, render, dotSpacing, dotRadius)
{
	if (render == FoldableDesignerGfxApp.EdgeRender.DEFAULT)
	{
		gfxPath.MoveTo(ptA);
		gfxPath.LineTo(ptB);
	}
	else if (render == FoldableDesignerGfxApp.EdgeRender.DOTTED)
	{
		gfxPath.AddPerforation(ptA, ptB, dotSpacing, dotRadius, {style:"circles"});
	}
	else if (render == FoldableDesignerGfxApp.EdgeRender.DASHED)
	{
		gfxPath.AddPerforation(ptA, ptB, dotSpacing, dotRadius, {style:"lines"});
	}
}

//-------------------------------------------------------------------------------------
//	Point Vector Add
//-------------------------------------------------------------------------------------
var PointVectorAdd = function(p, v, s) 
{
	var pt = {};
	pt.x = p.x + v.x * s;
	pt.y = p.y + v.y * s;
	
	return pt;
}

//-------------------------------------------------------------------------------------
//	Add Edge
//
//	Line info:
//		ptA
//		ptB
//		type (score, tab, slot)
//		desc
//-------------------------------------------------------------------------------------
var AddEdge = function(edgeList, ptA, ptB, edgeType, desc, config = undefined) 
{
	var edge = {};
	edge.edgeType = edgeType;
	edge.ptA = {x:ptA.x, y:ptA.y};
	edge.ptB = {x:ptB.x, y:ptB.y};
	edge.pairID = desc;
	if (config != undefined)
		edge.config = Object.assign({}, config);
	edgeList.push(edge);
}

//-------------------------------------------------------------------------------------
//	Close Edge
//		Marks the last edge in the list as the end of the current edge path.
//		Note that this is only recognized for SLOT, TAB, and CUT 
//		See usage in GenerateGfxDrawingFromEdgeList
//-------------------------------------------------------------------------------------
var CloseEdge = function(edgeList) 
{
	if (edgeList.length > 0)
	{
		edgeList[edgeList.length - 1].close = true;
	}
}

//-------------------------------------------------------------------------------------
//	Place Edge
//-------------------------------------------------------------------------------------
function PlaceEdge(edges, capsule, edgeType, desc)
{
	var angle = capsule.angle * 3.14159/180.0;
	var v = {x:capsule.len * Math.cos(angle), y:capsule.len * Math.sin(angle)};
	var ptB = PointVectorAdd(capsule.pt, v, 1);
	if (edgeType != -1)
		AddEdge(edges, capsule.pt, ptB, edgeType, desc);
	capsule.pt.x = ptB.x;
	capsule.pt.y = ptB.y;
}

//-------------------------------------------------------------------------------------
//	Corner Turn and regular Turn
//-------------------------------------------------------------------------------------
function CornerTurn(capsule, angle)
{
	capsule.angle += 180 - angle;
}

function Turn(capsule, angle)
{
	capsule.angle += angle;
}


function CopyCapsule(capsule)
{
	let c = {pt:{x:capsule.pt.x, y:capsule.pt.y}, angle:capsule.angle, len: capsule.len};
	return c;
}

function PushCapsule(capsule)
{
	if (capsule.stack == undefined)
		capsule.stack = [];
	
	capsule.stack.push(CopyCapsule(capsule));
}

function PopCapsule(capsule)
{
	if (capsule.stack != undefined)
	{
		if (capsule.stack.length > 0)
		{
			let c = capsule.stack.pop();
			
			capsule.pt.x = c.pt.x;
			capsule.pt.y = c.pt.y;
			capsule.angle = c.angle;
			capsule.len = c.len;
		}
	}
}

//-------------------------------------------------------------------------------------
//	Get Info
//
//	* Override to provide info to display
//-------------------------------------------------------------------------------------
FoldableDesignerGfxApp.prototype.GetInfo = function()
{
	var info = undefined;
	
	info = JSON.parse(JSON.stringify(this.settings.shape));
	
	return info;
}


//-------------------------------------------------------------------------------------
//
//	Foldable Shape Designer
//
//		Create plans for folding shapes
//
//-------------------------------------------------------------------------------------


//-------------------------------------------------------------------------------------
//	Constructor
//-------------------------------------------------------------------------------------
function FoldableShapeDesignerGfxApp()
{
	FoldableDesignerGfxApp.call(this, 
		"Foldable Shape Designer", 
		"FoldableShapeDesignerGfxAppId",
		FoldableShapeDesignerGfxApp.defaultSettings, 
		FoldableShapeDesignerGfxApp.controlGroups);
}

FoldableShapeDesignerGfxApp.prototype = Object.create(FoldableDesignerGfxApp.prototype);

Object.defineProperty(FoldableShapeDesignerGfxApp.prototype, 'constructor', {
	value: FoldableShapeDesignerGfxApp,
	enumerable: false,
	write: true });

//-------------------------------------------------------------------------------------
//	Default settings (in addition to the base class default settings)
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.defaultSettings =
{
	shape: "cube",
	size: 40,
	rotation: 0.0,
};

//-------------------------------------------------------------------------------------
//	Control Menus
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.shapeSelection = 
	[
		{ label: "Tetrahedron",				value: "tetra" },
		{ label: "Cube",					value: "cube" },
		{ label: "Octahedron",				value: "octa" },
		{ label: "Icosahedron",				value: "icosa" },
		{ label: "Icosahedron (2 pieces)",	value: "icosa-split" },
		{ label: "Dodecahedron",			value: "dodeca" },
		{ label: "Dodecahedron (2 pieces)",	value: "dodeca-split" },
		{ label: "Triacontahedron (1/5th)",	value: "triaconta" }
	];

//-------------------------------------------------------------------------------------
//	Controls and Control Groups
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.generalControls =
	[
		{ label: "Shape", 			type: "selection",	prop: "shape",		options: FoldableShapeDesignerGfxApp.shapeSelection	},
		{ label: "Size",			type: "number",		prop: "size",		min:0.1, max: 100, step:0.1, entry:"yes"	},
		{ label: "Rotation",		type: "number",		prop: "rotation",	min:-180, max: 180, step:0.1, entry:"yes"	}
	];


FoldableShapeDesignerGfxApp.controlGroups = 
	[
		{ label: "General",			controls: FoldableShapeDesignerGfxApp.generalControls	},
		{ label: "Tab Settings",	controls: FoldableDesignerGfxApp.tabControls		},
		{ label: "Slot Settings",	controls: FoldableDesignerGfxApp.slotControls		},
		{ label: "Score Settings",	controls: FoldableDesignerGfxApp.scoreControls		},
		{ label: "More",			controls: FoldableDesignerGfxApp.moreControls		}
	];

//-------------------------------------------------------------------------------------
//	Generate Cube Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateCubeFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Square corner
	var corner = 90;
	
	// Start point	
	var offset = {x: edgeLength, y: 10};
	var startPt = {x: edgeLength + offset.x, y: offset.y};
	
	// Start angle
	var a = this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	// Cut edges
	PlaceEdge(edges, capsule,TAB, "0");		// Top edge
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule,TAB, "1");		// Right of top square
	CornerTurn(capsule, 3*corner);
	PlaceEdge(edges, capsule,SLOT, "1");	// Top of right arm
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule,SLOT, "2");	// Right side of right arm
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule,SLOT, "3");	// Bottom of right arm
	CornerTurn(capsule, 3*corner);
	PlaceEdge(edges, capsule,TAB, "3");		// Right side
	PlaceEdge(edges, capsule,TAB, "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule,SLOT, "0");	// Bottom edge
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule,TAB, "4");		// Left side
	PlaceEdge(edges, capsule,TAB, "5");
	CornerTurn(capsule, 3*corner);
	PlaceEdge(edges, capsule,SLOT, "5");	// Bottom of left arm
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule,SLOT, "4");	// Left side of left arm
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule,SLOT, "6");	// Top of left arm
	CornerTurn(capsule, 3*corner);
	PlaceEdge(edges, capsule, TAB, "6");	// Left of top square
	CornerTurn(capsule, corner);

	// Score edges
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, MOVE, "");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE, "7");	// Inside square
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "8");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "9");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "9");
	Turn(capsule, 180);
	PlaceEdge(edges, capsule, MOVE, "");
	PlaceEdge(edges, capsule, MOVE, "");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE, "10");	// Bottom score line

	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Cube Fold (v1)
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateCubeFold_v1 = function(edgeLength)
{
	var edges = [];
	var el = edgeLength;
	var offset = {x: 10, y: 10};
	var x;
	var y;

	
	x = el + offset.x;
	y = offset.y;
	
	AddEdge(edges, {x:x, y:y}, {x:x + el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.TAB, "0");
	x+= el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y + el}, FoldableShapeDesignerGfxApp.EdgeType.TAB, "1");
	y+=el;
	AddEdge(edges, {x:x, y:y}, {x:x + el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "1");
	x+= el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y + el}, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "2");
	y+=el;
	AddEdge(edges, {x:x, y:y}, {x:x - el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "3");
	x -= el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y + el}, FoldableShapeDesignerGfxApp.EdgeType.TAB, "3");
	y+=el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y + el}, FoldableShapeDesignerGfxApp.EdgeType.TAB, "2");
	y+=el;
	AddEdge(edges, {x:x, y:y}, {x:x - el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "0");
	x -= el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y - el}, FoldableShapeDesignerGfxApp.EdgeType.TAB, "4");
	y -= el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y - el}, FoldableShapeDesignerGfxApp.EdgeType.TAB, "5");
	y -= el;
	AddEdge(edges, {x:x, y:y}, {x:x - el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "5");
	x -= el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y - el}, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "4");
	y -= el;
	AddEdge(edges, {x:x, y:y}, {x:x + el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "6");
	x += el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y - el}, FoldableShapeDesignerGfxApp.EdgeType.TAB, "6");
	y -= el;

	x = el + offset.x;
	y = el + offset.y;

	AddEdge(edges, {x:x, y:y}, {x:x + el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "7");
	y += el;
	AddEdge(edges, {x:x, y:y}, {x:x + el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "8");
	y += el;
	AddEdge(edges, {x:x, y:y}, {x:x + el, y:y}, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "9");

	x = el + offset.x;
	y = el + offset.y;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y + el}, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "10");
	x += el;
	AddEdge(edges, {x:x, y:y}, {x:x, y:y + el}, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "11");

	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Octahedron Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateTetrahedronFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Equilateral triangle corner
	var corner = 60;
	
	// Start point	
	var offset = {x: 10, y: 10};
	var startPt = {x: edgeLength + offset.x, y: offset.y};
	
	// Start angle
	var a = this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	// Cut edges
	PlaceEdge(edges, capsule, TAB, "0");
	PlaceEdge(edges, capsule, SLOT, "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB, "0");
	PlaceEdge(edges, capsule, SLOT, "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB, "0");
	PlaceEdge(edges, capsule, SLOT, "0");
	CornerTurn(capsule, corner);


	// Score lines
	PlaceEdge(edges, capsule, MOVE, "0");
	Turn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Octahedron Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateOctahedronFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Equilateral triangle corner
	var corner = 60;
	
	// Start point	
	var offset = {x: 10, y: 10};
	var startPt = {x: edgeLength + offset.x, y: offset.y};
	
	// Start angle
	var a = this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	// Cut edges
	PlaceEdge(edges, capsule, TAB, "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB, "1");
	CornerTurn(capsule, 4*corner);
	PlaceEdge(edges, capsule, SLOT, "1");
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, TAB, "2");
	CornerTurn(capsule, 4*corner);
	PlaceEdge(edges, capsule, SLOT, "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "0");
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, TAB, "3");
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, TAB, "4");
	CornerTurn(capsule, 4*corner);
	PlaceEdge(edges, capsule, SLOT, "4");
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SLOT, "3");
	CornerTurn(capsule, 2*corner);

	// Score lines
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	CornerTurn(capsule, -2*corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, MOVE, "");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, MOVE, "");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, MOVE, "");
	CornerTurn(capsule, -2*corner);
	PlaceEdge(edges, capsule, SCORE, "1");
	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Octahedron Fold (v1)
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateOctahedronFold_v1 = function(edgeLength)
{
	var edges = [];
	var el = edgeLength;
	var x;
	var y;
	var a = 60 * 3.14159 / 180.0;
	var h = {x:el, y:0};
	var d1 = {x:el * Math.cos(a), y:el * Math.sin(a)};
	var d2 = {x:-d1.x, y:d1.y};	
	var ptA = {x: el * Math.cos(a), y: 0};
	var ptB;
	
	ptB = PointVectorAdd(ptA, h, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.TAB, "0");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d2, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.TAB, "1");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d1, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "1");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d2, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.TAB, "2");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d1, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "2");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, h, -1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "0");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d1, -1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.TAB, "3");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d2, -1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.TAB, "4");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d1, -1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "4");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, d2, -1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SLOT, "3");
	ptA = ptB;

	var ptA = {x: el * Math.cos(a), y: 0};
	ptB = PointVectorAdd(ptA, d1, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "1");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, h, -1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "1");
	ptB = PointVectorAdd(ptA, d2, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "1");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, h, 1);
	AddEdge(edges, ptA, ptB, FoldableShapeDesignerGfxApp.EdgeType.SCORE, "1");
	ptB = PointVectorAdd(ptA, d1, 1);
	AddEdge(edges, ptA, ptB, FoldableDesignerGfxApp.EdgeType.SCORE, "1");
	ptA = ptB;
	ptB = PointVectorAdd(ptA, h, -1);
	AddEdge(edges, ptA, ptB, FoldableDesignerGfxApp.EdgeType.SCORE, "1");
	ptB = PointVectorAdd(ptA, d2, 1);
	AddEdge(edges, ptA, ptB, FoldableDesignerGfxApp.EdgeType.SCORE, "1");
	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Icosahedron Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateIcosahedronFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Equilateral triangle corner
	var corner = 60;
	
	// Start point	
	var offset = {x: 0, y: 0};
	var a = 3.14159/6; // 30 degrees
	var startPt = {x: edgeLength * Math.cos(a) + offset.x, y: offset.y + edgeLength * Math.sin(a) };
	
	// Start angle
	var a = 30.0 + this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	// Cut lines
	PlaceEdge(edges, capsule, TAB,  "0");	// Top two lines (on right)
	PlaceEdge(edges, capsule, TAB,  "1");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "2");	// First 'valley' on right
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "3");	// Second 'valley' on right
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "3");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "4");	// Third 'valley' on right
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "4");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "5");	// Fourth 'valley' on right
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "5");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "1");	// Bottom right line
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SLOT, "0");	// Bottom two lines (on left)
	PlaceEdge(edges, capsule, TAB, "6");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "7");	// First 'valley' on left, going up
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "7");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "8");	// Second 'valley' on left
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "8");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "9");	// Third 'valley' on left
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "9");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "10");	// Fourth 'valley' on left
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "10");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "6");	// Top left line
	CornerTurn(capsule, 2*corner);
	
	// Score lines
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SCORE,  "11");	// Straight down, five triangle edges
	PlaceEdge(edges, capsule, SCORE,  "12");
	PlaceEdge(edges, capsule, SCORE,  "13");
	PlaceEdge(edges, capsule, SCORE,  "14");
	PlaceEdge(edges, capsule, SCORE,  "15");
	
	CornerTurn(capsule, -2*corner);
	PlaceEdge(edges, capsule, MOVE,  "");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "16");	// Straight up, five triangle edges
	PlaceEdge(edges, capsule, SCORE,  "17");
	PlaceEdge(edges, capsule, SCORE,  "18");
	PlaceEdge(edges, capsule, SCORE,  "19");
	PlaceEdge(edges, capsule, SCORE,  "20");
	
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "16");	// Zig-zag down the middle
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "17");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "18");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "19");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "20");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "17");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "18");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "19");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "20");
	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Icosahedron Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateIcosahedronSplitFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Equilateral triangle corner
	var corner = 60;
	
	// Start point	
	var offset = {x: 0, y: 0};
	var a = 3.14159/6; // 30 degrees
	var startPt = {x: edgeLength * Math.cos(a) + offset.x, y: offset.y + edgeLength * Math.sin(a) };
	
	// Start angle
	var a = 30.0 + this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	// Cut lines
	PlaceEdge(edges, capsule, TAB,  "0");	// Top two lines (on right)
	PlaceEdge(edges, capsule, TAB,  "1");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "2");	// First 'valley' on right
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "3");	// Second 'valley' on right

	PlaceEdge(edges, capsule, SLOT, "3");	// Second 'valley' on right

	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, TAB, "8");	// Second 'valley' on left
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "8");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB, "9");	// Third 'valley' on left
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "9");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB, "10");	// Fourth 'valley' on left
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "10");
	CloseEdge(edges);


	// Score lines
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "11");	// Straight down, five triangle edges
	PlaceEdge(edges, capsule, SCORE,  "12");
	PlaceEdge(edges, capsule, SCORE,  "13");
	
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, MOVE,  "");
	CornerTurn(capsule, -2*corner);
	PlaceEdge(edges, capsule, SCORE,  "19");
	PlaceEdge(edges, capsule, SCORE,  "20");
	
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "16");	// Zig-zag down the middle
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "17");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "18");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "19");

	// Reset Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	capsule.angle = this.settings.rotation;
	capsule.len = edgeLength * 3 * Math.cos(Math.PI/6);
	PlaceEdge(edges, capsule, MOVE,  "10");
	
	capsule.angle = a;
	capsule.len = edgeLength;
	
	// Cut lines, second part
	PlaceEdge(edges, capsule, SLOT,  "0");	// Top two lines (on right)
	PlaceEdge(edges, capsule, TAB,  "1");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "2");	// First 'valley' on right
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB,  "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT, "3");	// Second 'valley' on right

	PlaceEdge(edges, capsule, TAB, "3");	// Second 'valley' on right

	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, TAB, "8");	// Second 'valley' on left
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "8");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB, "9");	// Third 'valley' on left
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "9");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, TAB, "10");	// Fourth 'valley' on left
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "10");
	CloseEdge(edges);
	
	// Score lines
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "11");	// Straight down, five triangle edges
	PlaceEdge(edges, capsule, SCORE,  "12");
	PlaceEdge(edges, capsule, SCORE,  "13");
	
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, MOVE,  "");
	CornerTurn(capsule, -2*corner);
	PlaceEdge(edges, capsule, SCORE,  "19");
	PlaceEdge(edges, capsule, SCORE,  "20");
	
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "16");	// Zig-zag down the middle
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "17");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "18");
	CornerTurn(capsule, -corner);
	PlaceEdge(edges, capsule, SCORE,  "19");

	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Icosahedron Fold (v1)
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateIcosahedronFold_v1 = function(edgeLength)
{
	var edges = [];
	var el = edgeLength;
	var x;
	var y;
	var a = 30 * 3.14159 / 180.0;
	var v = {x:0, y:el};
	var d1 = {x:el * Math.cos(a), y:el * Math.sin(a)};
	var d2 = {x:-d1.x, y:d1.y};	
	var ptA = {x: el * Math.cos(a), y: 0};
	var ptB;
	
	function calcAndAdd(ptA, v, s, edgeType, desc) {
		var ptB = PointVectorAdd(ptA, v, s);
		AddEdge(edges, ptA, ptB, edgeType, desc);
		return ptB;
	}
	
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.TAB,  "0");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.TAB,  "1");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SLOT, "1");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.TAB,  "2");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SLOT, "2");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.TAB,  "3");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SLOT, "3");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.TAB,  "4");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SLOT, "4");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.TAB,  "5");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SLOT, "5");
	ptA = calcAndAdd(ptA, d1,-1, FoldableDesignerGfxApp.EdgeType.SLOT, "0");
	ptA = calcAndAdd(ptA, d1,-1, FoldableDesignerGfxApp.EdgeType.SLOT, "6");
	ptA = calcAndAdd(ptA, d2,-1, FoldableDesignerGfxApp.EdgeType.TAB,  "6");
	ptA = calcAndAdd(ptA, d1,-1, FoldableDesignerGfxApp.EdgeType.SLOT, "7");
	ptA = calcAndAdd(ptA, d2,-1, FoldableDesignerGfxApp.EdgeType.TAB,  "7");
	ptA = calcAndAdd(ptA, d1,-1, FoldableDesignerGfxApp.EdgeType.SLOT, "8");
	ptA = calcAndAdd(ptA, d2,-1, FoldableDesignerGfxApp.EdgeType.TAB,  "8");
	ptA = calcAndAdd(ptA, d1,-1, FoldableDesignerGfxApp.EdgeType.SLOT, "9");
	ptA = calcAndAdd(ptA, d2,-1, FoldableDesignerGfxApp.EdgeType.TAB,  "9");
	ptA = calcAndAdd(ptA, d1,-1, FoldableDesignerGfxApp.EdgeType.SLOT, "10");
	ptA = calcAndAdd(ptA, d2,-1, FoldableDesignerGfxApp.EdgeType.TAB,  "10");
	
	ptA = {x: el * Math.cos(a), y: 0};
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "11");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "12");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "13");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "14");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "15");
	
	ptA = {x: el * Math.cos(a), y: 0};
	ptA = PointVectorAdd(ptA, d1, 1);
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "16");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "17");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "18");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "19");
	ptA = calcAndAdd(ptA, v, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "20");
	
	ptA = {x: el * Math.cos(a), y: 0};
	ptA = PointVectorAdd(ptA, d1, 1);
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "16");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "17");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "18");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "19");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "20");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "17");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "18");
	ptA = calcAndAdd(ptA, d1, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "19");
	ptA = calcAndAdd(ptA, d2, 1, FoldableDesignerGfxApp.EdgeType.SCORE,  "20");

	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Dodecahedron Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateDodecahedronFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Equilateral triangle corner
	var corner = 180 - 72;
	
	// Start point	
	var offset = {x: 10, y: 10};
	var startPt = {x: edgeLength + offset.x, y: offset.y };
	
	// Start angle
	var a = this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	
	PlaceEdge(edges, capsule, TAB,  "0");	// First top horizontal edge
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "1");	// First valley, left side
	
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SCORE,  "0");	// Score, first inside pentagon
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, 2*corner);
	
	PlaceEdge(edges, capsule, SLOT, "1");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "3");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "4");
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "4");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "5");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "6");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "7");
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "7");
	Turn(capsule, -36);
	
	
	PlaceEdge(edges, capsule, SLOT,  "6");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "8");
	Turn(capsule, -36);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	Turn(capsule, -36);
	PlaceEdge(edges, capsule, SLOT, "8");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "5");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "3");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "9");
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "9");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "10");
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "10");
	CornerTurn(capsule, corner);
	
	PlaceEdge(edges, capsule, SLOT,  "18");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "11");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "12");
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "12");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "13");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "14");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "15");
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "15");
	
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE, "0");
	Turn(capsule, 180);
	PlaceEdge(edges, capsule, MOVE, "");
	CornerTurn(capsule, corner);
		
	PlaceEdge(edges, capsule, TAB,  "14");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "16");
	CornerTurn(capsule, 3*corner);
	
	
	PlaceEdge(edges, capsule, SLOT,  "16");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "13");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "11");
	CornerTurn(capsule, corner);
	
	PlaceEdge(edges, capsule, TAB, "17");
	CornerTurn(capsule, 3*corner);
	PlaceEdge(edges, capsule, SLOT,  "17");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "18");
	

	return edges;
}

//-------------------------------------------------------------------------------------
//	Generate Dodecahedron Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateDodecahedronSplitFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Equilateral triangle corner
	var corner = 180 - 72;
	
	// Start point	
	var offset = {x: 10, y: 10};
	var startPt = {x: edgeLength + offset.x, y: offset.y };
	
	// Start angle
	var a = this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	// First Piece
	PlaceEdge(edges, capsule, TAB,  "0");	// First top horizontal edge
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "1");	// First valley, enter side
	
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SCORE,  "0");	// Score, first inside pentagon
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, 2*corner);
	
	PlaceEdge(edges, capsule, SLOT, "1");	// First valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "3");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "4");	// Second valley, enter side
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "4");	// Second valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "5");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "6");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "7");	// Third valley, enter side
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "7");	// Third valley, exit side
	CornerTurn(capsule, corner);		
	PlaceEdge(edges, capsule, TAB,  "14");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "16");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "16");	// Fourth valley, enter side
	CornerTurn(capsule, 3*corner);
	
	
	PlaceEdge(edges, capsule, SLOT,  "16");	// Fourth valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "13");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "11");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB, "17");	// Fifth valley, enter side
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT,  "17");	// Fifth valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "18");
	CornerTurn(capsule, corner);
	CloseEdge(edges);

	// Update position for second piece
	var distance = edgeLength * 4.5; 
	var angle = capsule.angle + 90;
	capsule.pt.x += distance * Math.cos(angle * 3.141519/180.0); 
	capsule.pt.y += distance * Math.sin(angle * 3.141519/180.0); 

	// Second Piece
	PlaceEdge(edges, capsule, SLOT,  "0");	// First top horizontal edge
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "1");	// First valley, enter side
	
	CornerTurn(capsule, 2*corner);
	PlaceEdge(edges, capsule, SCORE,  "0");	// Score, first inside pentagon
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SCORE,  "0");
	CornerTurn(capsule, 2*corner);
	
	PlaceEdge(edges, capsule, SLOT, "1");	// First valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "2");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "3");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "4");	// Second valley, enter side
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "4");	// Second valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "5");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "6");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "7");	// Third valley, enter side
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT, "7");	// Third valley, exit side
	CornerTurn(capsule, corner);		
	PlaceEdge(edges, capsule, SLOT,  "14");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "16");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB,  "16");	// Fourth valley, enter side
	CornerTurn(capsule, 3*corner);
	
	
	PlaceEdge(edges, capsule, SLOT,  "16");	// Fourth valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "13");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "11");
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, TAB, "17");	// Fifth valley, enter side
	CornerTurn(capsule, 3*corner);
	
	PlaceEdge(edges, capsule, SLOT,  "17");	// Fifth valley, exit side
	CornerTurn(capsule, corner);
	PlaceEdge(edges, capsule, SLOT,  "18");


	return edges;
}

//-------------------------------------------------------------------------------------
//	Generate Dodecahedron Fold (v1)
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateDodecahedronFold_v1 = function(edgeLength)
{
	var offset = {x: 10, y: 10};
	var edges = [];
	var el = Number(edgeLength);
	var x;
	var y;
	//var a = 108.0; //(180 - 360 % 5);
	var a = 0.0;
	var aOutside = 72;
	var aInside = 324-360;
	aInside = -144;
	var v = {x:0, y:el};
	var ptA = {x: el + offset.x, y: offset.y};
	
	function calcAngleAndAdd(ptA, angle, len, edgeType, desc) {
		angle *= 3.14159/180.0;
		var v = {x:len * Math.cos(angle), y:len * Math.sin(angle)};
		var ptB = PointVectorAdd(ptA, v, 1);
		if (edgeType != -1)
			AddEdge(edges, ptA, ptB, edgeType, desc);
		return ptB;
	}
	
	a += this.settings.rotation;
	
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "1");
	
	a += -36;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += -36;
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "1");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "2");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "3");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "4");
	a += aInside
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "4");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "5");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "6");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "7");
	a += aInside
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "7");
	a += -36;
	
	
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "6");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "8");
	a += -36;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SCORE,  "0");
	a += -36;
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "8");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "5");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "3");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "9");
	a += aInside
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "9");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "2");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "0");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "10");
	a += aInside
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "10");
	a += aOutside;
	
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "18");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "11");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "12");
	a += aInside
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "12");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "13");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "14");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "15");
	a += aInside
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SLOT, "15");
	//a += -36
	
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.SCORE, "0");
	a += 180;
	ptA = calcAngleAndAdd(ptA, a, el, -1, "1");
	a += aOutside;
		
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "14");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "16");
	a += aInside
	
	
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "16");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "13");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "11");
	a += aOutside
	
	ptA = calcAngleAndAdd(ptA, a, el, FoldableDesignerGfxApp.EdgeType.TAB, "17");
	a += aInside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.SLOT,  "17");
	a += aOutside;
	ptA = calcAngleAndAdd(ptA, a,   el, FoldableDesignerGfxApp.EdgeType.TAB,  "18");
	

	return edges;
}

//-------------------------------------------------------------------------------------
//	Generate Triacontahedron Fold
//		One-fifth of a triacontahedron
//
//		http://mathworld.wolfram.com/GoldenRhombus.html
//		http://mathworld.wolfram.com/RhombicTriacontahedron.html 
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateTriacontahedronFold = function(edgeLength)
{
	// Edge list
	var edges = [];

	// Golden rhombus angles
	var sharpCorner = (31.7175 * 2);
	var wideCorner = 180.0 - sharpCorner;
	
	// Start point	
	var offset = {x: 10, y: 10};
	var startPt = {x: edgeLength + offset.x, y: offset.y};
	
	// Start angle
	var a = 90.0 - (sharpCorner / 2.0);
	a += this.settings.rotation;

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: edgeLength};
	
	// Cut lines
	PlaceEdge(edges, capsule, TAB,  "1");
	CornerTurn(capsule, 2*wideCorner);	
	PlaceEdge(edges, capsule, TAB,  "2");
	CornerTurn(capsule, 2*sharpCorner);
	PlaceEdge(edges, capsule, TAB,  "3");
	CornerTurn(capsule, 2 * wideCorner);
	PlaceEdge(edges, capsule, TAB,  "4");
	CornerTurn(capsule, sharpCorner);
	PlaceEdge(edges, capsule, TAB,  "5");
	CornerTurn(capsule, 2 * wideCorner);
	PlaceEdge(edges, capsule, TAB,  "6");
	CornerTurn(capsule, sharpCorner);
	PlaceEdge(edges, capsule, SLOT,  "6");
	CornerTurn(capsule, wideCorner);
	PlaceEdge(edges, capsule, SLOT,  "5");
	CornerTurn(capsule, 3 * sharpCorner);
	PlaceEdge(edges, capsule, CUT,  "7");
	CornerTurn(capsule, 3 * wideCorner);
	PlaceEdge(edges, capsule, CUT,  "7");
	CornerTurn(capsule, sharpCorner);
	PlaceEdge(edges, capsule, SLOT,  "4");
	CornerTurn(capsule, wideCorner);
	PlaceEdge(edges, capsule, SLOT,  "3");
	CornerTurn(capsule, 3 * sharpCorner);
	PlaceEdge(edges, capsule, SLOT,  "2");
	CornerTurn(capsule, wideCorner);
	PlaceEdge(edges, capsule, SLOT,  "1");	
	CornerTurn(capsule, sharpCorner);
		
	// Score lines
	PlaceEdge(edges, capsule, MOVE,  "-");
	CornerTurn(capsule, wideCorner);
	PlaceEdge(edges, capsule, SCORE,  "-");
	CornerTurn(capsule, -sharpCorner);
	PlaceEdge(edges, capsule, SCORE,  "-");
	CornerTurn(capsule, -wideCorner);
	PlaceEdge(edges, capsule, SCORE,  "-");
	CornerTurn(capsule, sharpCorner);
	PlaceEdge(edges, capsule, MOVE,  "-");
	CornerTurn(capsule, wideCorner);
	PlaceEdge(edges, capsule, SCORE,  "-");
	CornerTurn(capsule, -sharpCorner);
	PlaceEdge(edges, capsule, SCORE,  "-");
	
	return edges;
}

//-------------------------------------------------------------------------------------
//	Generate Triacontahedron Fold (v1)
//
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.GenerateTriacontahedronFold_v1 = function(edgeLength)
{
	var offset = {x: 10, y: 10};
	var edges = [];
	var el = Number(edgeLength);
	var a = 0.0;

	let TAB   = FoldableDesignerGfxApp.EdgeType.TAB;
	let SLOT  = FoldableDesignerGfxApp.EdgeType.SLOT;
	let SCORE = FoldableDesignerGfxApp.EdgeType.SCORE;
	let CUT   = FoldableDesignerGfxApp.EdgeType.CUT;
	let MOVE  = -1;
	
	var sharpCorner = (31.7175 * 2);
	var wideCorner = 180.0 - sharpCorner;
	
	
	var ptA = {x: el + offset.x, y: offset.y};
	
	function calcAngleAndAdd(ptA, angle, len, edgeType, desc) {
		angle *= 3.14159/180.0;
		var v = {x:len * Math.cos(angle), y:len * Math.sin(angle)};
		var ptB = PointVectorAdd(ptA, v, 1);
		if (edgeType != -1)
			AddEdge(edges, ptA, ptB, edgeType, desc);
		return ptB;
	}
	
	a = 90.0 - (sharpCorner / 2.0);
	a += this.settings.rotation;
	
	ptA = calcAngleAndAdd(ptA, a,   el, TAB,  "1");
	
	a += (180.0 - 2 * wideCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, TAB,  "2");
	
	a += (180.0 - 2 * sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, TAB,  "3");
	
	a += (180.0 - 2 * wideCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, TAB,  "4");
	
	a += (180.0 - sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, TAB,  "5");
	
	a += (180.0 - 2 * wideCorner);
		
	ptA = calcAngleAndAdd(ptA, a,   el, TAB,  "6");
	
	a += (180.0 - sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SLOT,  "6");
	
	a += (180.0 - wideCorner);
		
	ptA = calcAngleAndAdd(ptA, a,   el, SLOT,  "5");
	
	a += (180.0 - 3 * sharpCorner);
		
	ptA = calcAngleAndAdd(ptA, a,   el, CUT,  "7");
	
	a += (180.0 - 3 * wideCorner);
		
	ptA = calcAngleAndAdd(ptA, a,   el, CUT,  "7");
	
	a += (180.0 - sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SLOT,  "4");
	
	a += (180.0 - wideCorner);
		
	ptA = calcAngleAndAdd(ptA, a,   el, SLOT,  "3");
	
	a += (180.0 - 3 * sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SLOT,  "2");
	
	a += (180.0 - wideCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SLOT,  "1");
	
	a += (180.0 - sharpCorner);
		
	// Score lines
	
	ptA = calcAngleAndAdd(ptA, a,   el, MOVE,  "-");
	
	a += (180.0 - wideCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SCORE,  "-");
	
	a += (180.0 + sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SCORE,  "-");
	
	a += (180.0 + wideCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SCORE,  "-");
	
	a += (180.0 - sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, MOVE,  "-");
	
	a += (180.0 - wideCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SCORE,  "-");
	
	a += (180.0 + sharpCorner);
	
	ptA = calcAngleAndAdd(ptA, a,   el, SCORE,  "-");
	
	
	
	
	
	return edges;
}

//-------------------------------------------------------------------------------------
//	Generate Shape Fold
//-------------------------------------------------------------------------------------
FoldableShapeDesignerGfxApp.prototype.Generate = function()
{
	this.EdgeList = [];
	
	var size = this.settings.size;
	if (size >= 0.1)
	{
		if (this.settings.shape == "cube")
			this.EdgeList = this.GenerateCubeFold(size);
		else if (this.settings.shape == "tetra")
			this.EdgeList = this.GenerateTetrahedronFold(size);
		else if (this.settings.shape == "octa")
			this.EdgeList = this.GenerateOctahedronFold(size);
		else if (this.settings.shape == "dodeca")
			this.EdgeList = this.GenerateDodecahedronFold(size);
		else if (this.settings.shape == "dodeca-split")
			this.EdgeList = this.GenerateDodecahedronSplitFold(size);
		else if (this.settings.shape == "icosa")
			this.EdgeList = this.GenerateIcosahedronFold(size);
		else if (this.settings.shape == "icosa-split")
			this.EdgeList = this.GenerateIcosahedronSplitFold(size);
		else if (this.settings.shape == "triaconta")
			this.EdgeList = this.GenerateTriacontahedronFold(size);
	}
	
	this.gfxDrawing = this.GenerateGfxDrawingFromEdgeList(this.EdgeList, this.settings);
}

//-------------------------------------------------------------------------------------
//	Public
//-------------------------------------------------------------------------------------
export { FoldableShapeDesignerGfxApp };



//-------------------------------------------------------------------------------------
//
//	Foldable Box Designer
//
//		Create plans for folding boxes
//
//-------------------------------------------------------------------------------------


//-------------------------------------------------------------------------------------
//	Constructor
//-------------------------------------------------------------------------------------
function FoldableBoxDesignerGfxApp()
{
	FoldableDesignerGfxApp.call(this, 
		"Foldable Box Designer", 
		"FoldableBoxDesignerGfxAppId",
		FoldableBoxDesignerGfxApp.defaultSettings, 
		FoldableBoxDesignerGfxApp.controlGroups);
}

FoldableBoxDesignerGfxApp.prototype = Object.create(FoldableDesignerGfxApp.prototype);

Object.defineProperty(FoldableBoxDesignerGfxApp.prototype, 'constructor', {
	value: FoldableBoxDesignerGfxApp,
	enumerable: false,
	write: true });

//-------------------------------------------------------------------------------------
//	Default settings (in addition to the base class default settings)
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.defaultSettings =
{
	shape: "box",
	rotate90: false,
	includeTop: true,
	topEdgeStyle: "none",
	frontEdgeStyle: "tabOnly",
	bottomEdgeStyle: "flaps",
	width: 60,
	depth: 40,
	height: 50,
	rotation: 0,
	sides: 5,
	separateBase: false,
	pyramidBaseEdges: "tabsAndSlots",
	pyramidTopEdges: "tabAndSlot",
	pyramidSideEdges: "tabAndSlot",
	pyramidBottomStyle: "attached"
};

//-------------------------------------------------------------------------------------
//	Control Menus
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.shapeSelection = 
	[
		{ label: "Basic Box",			value: "box" },
		{ label: "Pyramid Box",			value: "pyrabox" },
		{ label: "Pyramid",				value: "pyramid" },
		{ label: "Multi-sided box",		value: "polybox" }
	];

FoldableBoxDesignerGfxApp.sideEdgeSelection = 
	[
		{ label: "Plain",			value: "none" },
		{ label: "Tabs",			value: "tabs" },
		{ label: "Slots",			value: "slots" },
		{ label: "Flaps",			value: "flaps" }
	];

FoldableBoxDesignerGfxApp.bottomStyleSelection =
	[
		{ label: "Attached",		value: "attached" },
		{ label: "Separate",		value: "separate" },
		{ label: "None",			value: "none" }
	];

FoldableBoxDesignerGfxApp.frontEdgeSelection = 
	[
		{ label: "Tab and Slot",	value: "tabAndSlot" },
		{ label: "Tab only",		value: "tabOnly" }
	];

FoldableBoxDesignerGfxApp.baseEdgeSelection = 
	[
		{ label: "Plain",			value: "none" },
		{ label: "Tabs and Slots",	value: "tabsAndSlots" },
		{ label: "Tabs only",		value: "tabsOnly" },
		{ label: "Slots and Tabs",	value: "slotsAndTabs" },
		{ label: "Flaps",			value: "flaps" }
	];

//-------------------------------------------------------------------------------------
//	Controls and Control Groups
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.generalControls =
	[
		{ label: "Shape", 			type: "selection",	prop: "shape",		options: FoldableBoxDesignerGfxApp.shapeSelection	},
		{ label: "Width",			type: "number",		prop: "width",		min:5, max: 100, step:0.1, entry:"yes"	},
		{ label: "Depth",			type: "number",		prop: "depth",		min:5, max: 100, step:0.1, entry:"yes"	},
		{ label: "Height",			type: "number",		prop: "height",		min:5, max: 100, step:0.1, entry:"yes"	},
		{ label: "Sides",			type: "number",		prop: "sides",		min:3, max: 12,	 step:1,   entry:"no"	},
		{ label: "Rotate 90°",		type: "boolean",	prop: "rotate90"	},
	];

FoldableBoxDesignerGfxApp.topBottomControls =
	[
		{ label: "Include Top",		type: "boolean",	prop: "includeTop"	},
		{ label: "Top Sides", 		type: "selection",	prop: "topEdgeStyle",		options: FoldableBoxDesignerGfxApp.sideEdgeSelection	},
		{ label: "Front Style", 	type: "selection",	prop: "frontEdgeStyle",		options: FoldableBoxDesignerGfxApp.frontEdgeSelection	},
		{ label: "Bottom Sides", 	type: "selection",	prop: "bottomEdgeStyle",	options: FoldableBoxDesignerGfxApp.sideEdgeSelection	}
	];

FoldableBoxDesignerGfxApp.pyramidControls =
	[
		{ label: "Top Edges", 		type: "selection",	prop: "pyramidTopEdges",		options: FoldableBoxDesignerGfxApp.frontEdgeSelection	},
		{ label: "Side Edges", 		type: "selection",	prop: "pyramidSideEdges",		options: FoldableBoxDesignerGfxApp.frontEdgeSelection	},
		{ label: "Base Edges", 		type: "selection",	prop: "pyramidBaseEdges",		options: FoldableBoxDesignerGfxApp.baseEdgeSelection	},
		{ label: "Bottom",			type: "selection",	prop: "pyramidBottomStyle",		options: FoldableBoxDesignerGfxApp.bottomStyleSelection	},
		{ label: "Alternate Layout",type: "boolean",	prop: "alternateLayout"	}
	];


FoldableBoxDesignerGfxApp.controlGroups = 
	[
		{ label: "General",				controls: FoldableBoxDesignerGfxApp.generalControls	},
		{ label: "Basic Box",			controls: FoldableBoxDesignerGfxApp.topBottomControls	},
		{ label: "Pyramid",				controls: FoldableBoxDesignerGfxApp.pyramidControls	},
		{ label: "Tab Settings",		controls: FoldableDesignerGfxApp.tabControls		},
		{ label: "Slot Settings",		controls: FoldableDesignerGfxApp.slotControls		},
		{ label: "Flap Settings",		controls: FoldableDesignerGfxApp.flapControls		},
		{ label: "Score Settings",		controls: FoldableDesignerGfxApp.scoreControls		},
		{ label: "More",				controls: FoldableDesignerGfxApp.moreControls		}
	];

//-------------------------------------------------------------------------------------
//	Generate Rectangular Box Fold
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.prototype.GenerateRectangularBoxFold = function()
{
	// Edge list
	var edges = [];
	
	// Settings
	var includeTop = this.settings.includeTop;
	var topEdgeStyle = TAB; 
	var topMatchStyle = SLOT; 
	var bottomEdgeStyle = TAB;
	var bottomMatchStyle = SLOT; 
	var frontEdgeStyle = TAB; 
	var frontMatchStyle = SLOT; 
	
	if (this.settings.topEdgeStyle == "none")
	{
		topEdgeStyle = CUT;
		topMatchStyle = CUT;
	}
	else if (this.settings.topEdgeStyle == "flaps")
	{
		topEdgeStyle = FLAP;
		topMatchStyle = CUT;
	}
	else if (this.settings.topEdgeStyle == "slots")
	{
		topEdgeStyle = SLOT;
		topMatchStyle = TAB;
	}

	if (this.settings.bottomEdgeStyle == "none")
	{
		bottomEdgeStyle = CUT;
		bottomMatchStyle = CUT; 
	}
	else if (this.settings.bottomEdgeStyle == "flaps")
	{
		bottomEdgeStyle = FLAP;
		bottomMatchStyle = CUT; 
	}
	else if (this.settings.bottomEdgeStyle == "slots")
	{
		bottomEdgeStyle = SLOT;
		bottomMatchStyle = TAB; 
	}
	
	if (this.settings.frontEdgeStyle == "tabOnly")
	{
		frontMatchStyle = CUT; 
	}

	// Square corner
	var corner = 90;
	
	//
	var width  = this.settings.width;
	var depth  = this.settings.depth;
	var height = this.settings.height;
	
	
	// Start angle
	var a = this.settings.rotate90 ? 90.0 : 0.0;

	// Start point	
	var offset = {x: 10, y: 10};
	offset.x += (this.settings.rotate90) ? (height + depth) : 0.0;
	offset.y += (this.settings.rotate90) ? 0 : depth;
	var startPt = {x: offset.x, y: offset.y};
	
	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: 0};
	
	// Cut edges
	capsule.len = depth;
	PlaceEdge(edges, capsule, includeTop?topEdgeStyle:CUT, "0");		// Unfolded: top of left side (folded: top of right side)
	
	PushCapsule(capsule);						
	CornerTurn(capsule, 90);
	capsule.len = height;
	PlaceEdge(edges, capsule, SCORE, "0");				// Vertical score line
	PopCapsule(capsule);
	
	capsule.len = width;
	PlaceEdge(edges, capsule, includeTop?frontMatchStyle:CUT, "1");		// Unfolded: top of 2nd rect (folder: top of front)
	
	PushCapsule(capsule);
	CornerTurn(capsule, 90);
	capsule.len = height;
	PlaceEdge(edges, capsule, SCORE, "0");				// Vertical score line
	PopCapsule(capsule);

	capsule.len = depth;
	PlaceEdge(edges, capsule, includeTop?topEdgeStyle:CUT, "0");// Unfolded: top of 3rd rect (folded: top of left side)
	
	PushCapsule(capsule);
	CornerTurn(capsule, 90);
	capsule.len = height;
	PlaceEdge(edges, capsule, SCORE, "0");				// Vertical score line
	PopCapsule(capsule);

	if (includeTop)
	{
		PushCapsule(capsule);
		capsule.len = width;
		PlaceEdge(edges, capsule, SCORE, "0");			// Horizontal score line
		PopCapsule(capsule);

		Turn(capsule, -corner);
		capsule.len = depth;
		PlaceEdge(edges, capsule, topMatchStyle, "0");	// (folded: left edge of top)

		Turn(capsule, corner);
		capsule.len = width;
		PlaceEdge(edges, capsule, frontEdgeStyle, "1");	// (folded: front edge of top)

		Turn(capsule, corner);
		capsule.len = depth;
		PlaceEdge(edges, capsule, topMatchStyle, "0");	// (folded: right edge of top)
	}
	else
	{
		capsule.len = width;
		PlaceEdge(edges, capsule, CUT, "0");			// Unfolded: top of 4th rect (folded: top of back side)

		Turn(capsule, corner);
	}
	
	capsule.len = height;
	PlaceEdge(edges, capsule, TAB, "0");				// (folded: right edge of back)

	capsule.len = depth;
	PlaceEdge(edges, capsule, bottomMatchStyle, "0");	// (folded: right edge of bottom)
			
	Turn(capsule, corner);
	capsule.len = width;
	PlaceEdge(edges, capsule, TAB, "1");				// (folded: front edge of bottom)

	Turn(capsule, corner);
	capsule.len = depth;
	PlaceEdge(edges, capsule, bottomMatchStyle, "1");	// (folded: left edge of bottom)

	PushCapsule(capsule);
	CornerTurn(capsule, 90);
	capsule.len = width;
	PlaceEdge(edges, capsule, SCORE, "0");				// Horizontal score
	PopCapsule(capsule);

	Turn(capsule, -corner);
	capsule.len = depth;
	PlaceEdge(edges, capsule,bottomEdgeStyle, "1");		// (folded: bottom edge of left side)

	capsule.len = width;
	PlaceEdge(edges, capsule, SLOT, "1");				// (folder: bottom edge of front)

	capsule.len = depth;
	PlaceEdge(edges, capsule, bottomEdgeStyle, "0");	// (folded: bottom edge of right side)

	Turn(capsule, corner);
	capsule.len = height;
	PlaceEdge(edges, capsule, SLOT, "0");				// Unfolded: left side (folded: back edge of right side)

	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Pyramid Box Fold
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.prototype.GeneratePyramidBoxFold = function()
{
	// Edge list
	var edges = [];
	
	// Settings
	var width  = this.settings.width;
	var baseHeight = this.settings.height;

	var topHeight = this.settings.depth;
	var triangleEdgeLen = Math.sqrt(width * width/4 + topHeight * topHeight);
	
	var bottomAngle = Math.atan((topHeight*2)/width) * 180.0 / Math.PI;
	var topAngle = 180 - 2 * bottomAngle;

	// 
	var sides = this.settings.sides;
	if (sides * topAngle >= 360)
	{
		sides = Math.floor(360/topAngle);
		this.generateWarningStr = "Triangle is too short or too wide for a pyramid with " + this.settings.sides + " sides.";
	}
	
	// Start angle
	var a = 0;
	
	if (!this.settings.alternateLayout)
	{
		a = this.settings.rotate90 ? 90.0 : 0.0;
	}
	else
	{
		// Start angle
		var sweepAngle = sides * topAngle;
		var splitAngle = 360 - sweepAngle;
		a = (90 - splitAngle/2);
		a += (this.settings.rotate90 ? -90.0 : 0.0);
	}
	
	// Start point	
	var startPt;
	
	if (!this.settings.alternateLayout)
	{
		var offset = {x: 10, y: 10};
		offset.x += (this.settings.rotate90) ? (2 * width) : 0;
		offset.y += (this.settings.rotate90) ? 0 : (topHeight);
		startPt = {x: offset.x, y: offset.y};
	}
	else
	{
		// Start point	
		var offset = {x: 50, y: 50};
		offset.x += triangleEdgeLen;
		offset.y += triangleEdgeLen;
		startPt = {x: offset.x, y: offset.y};
	}
	
	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: 0};
	
	// Base and bottom edges
	var baseEdgeStyle = TAB;
	var bottomEdgeStyle = SLOT;
	var separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	var separateBase = (this.settings.pyramidBottomStyle != "attached");
	
	if (this.settings.pyramidBaseEdges == "tabsAndSlots")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = SLOT;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "slotsAndTabs")
	{
		baseEdgeStyle = SLOT;
		bottomEdgeStyle = TAB;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "tabsOnly")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.tabDepth;
	}
	else if (this.settings.pyramidBaseEdges == "flaps")
	{
		baseEdgeStyle = FLAP;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.flapDepth;
	}
	else if (this.settings.pyramidBaseEdges == "none")
	{
		baseEdgeStyle = CUT;
		bottomEdgeStyle = CUT;
		separateBaseOffset = 0;
	}
	
	// Pyramid edges
	var pyramidEdgeStyle = TAB;
	var pyramidMatchStyle = (this.settings.pyramidTopEdges == "tabAndSlot") ? SLOT : CUT;

	// Base and bottom edges
	var sideEdgeStyle = TAB;
	var sideMatchStyle = (this.settings.pyramidSideEdges == "tabAndSlot") ? SLOT : CUT;;
	
	// Separate base offset
	var xxx = 0;


	if (!this.settings.alternateLayout)
	{
		// Layout: Separate triangles for pyramid, connected boxes for base
		
		// Top triangles of pyramid
		for (var k = 0; k < sides; k++)
		{
			PushCapsule(capsule);
			capsule.len = width;
			PlaceEdge(edges, capsule, SCORE, "0");
			PopCapsule(capsule);

			Turn(capsule, -bottomAngle);
		
			capsule.len = triangleEdgeLen;
			PlaceEdge(edges, capsule, pyramidEdgeStyle, "0");
		
			CornerTurn(capsule, topAngle);
			PlaceEdge(edges, capsule, pyramidMatchStyle, "0");

			Turn(capsule, -bottomAngle);
		}
	
		// Vertical edge
		Turn(capsule, 90);
		capsule.len = baseHeight;
		PlaceEdge(edges, capsule, TAB, "0");
	
		// Bottom edges
		let baseEdge = separateBase ? -1 : Math.floor(sides/2);
	
		Turn(capsule, 90);
		for (var k = 0; k < sides; k++)
		{
			if (k != baseEdge)
			{
				capsule.len = width;
				PlaceEdge(edges, capsule, baseEdgeStyle, "0");
			}
			else
			{
				PushCapsule(capsule);
				capsule.len = width;
				PlaceEdge(edges, capsule, SCORE, "0");
				PopCapsule(capsule);
			
				CornerTurn(capsule, -360/sides);
				for (var kk = 0; kk < sides - 1; kk++)
				{
					PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
					Turn(capsule, 360/sides);
				}
				Turn(capsule, 180);

			}

			if (k < sides -1)
			{
				PushCapsule(capsule);
				Turn(capsule, 90);
				capsule.len = baseHeight;
				PlaceEdge(edges, capsule, SCORE, "0");
				PopCapsule(capsule);
			}
		}
	
		Turn(capsule, 90);
		capsule.len = baseHeight;
		PlaceEdge(edges, capsule, SLOT, "0");
	
	}
	else
	{
		// Layout: Connected triangles for pyramid, separate boxes for base

		capsule.len = triangleEdgeLen;
		Turn(capsule, 180);
		PlaceEdge(edges, capsule, MOVE, "0");
		Turn(capsule, 180);
		
		// Start. First open edge of the pyramid
		capsule.len = triangleEdgeLen;
		PlaceEdge(edges, capsule, pyramidEdgeStyle, "0");

		// Add slots between the top triangles of pyramid
		for (var k = 1; k < sides; k++)
		{
			PushCapsule(capsule);
			CornerTurn(capsule, k * topAngle );
			PlaceEdge(edges, capsule, SCORE, "0");
			PopCapsule(capsule);
		}

		// Other open edge of the pyramid
		CornerTurn(capsule, sides * topAngle );
		PlaceEdge(edges, capsule, pyramidMatchStyle, "0");
	
		// Turn to the outside edge of the base
		CornerTurn(capsule, bottomAngle + 90);
	
		// Bottom edges
		let baseEdge = separateBase ? -1 : Math.floor(sides/2);
	
		for (var k = 0; k < sides; k++)
		{
			PushCapsule(capsule);
			CornerTurn(capsule, 90);
			capsule.len = width;
			PlaceEdge(edges, capsule, SCORE, "0");
			PopCapsule(capsule);

			capsule.len = baseHeight;
			PlaceEdge(edges, capsule, sideEdgeStyle, "0");
			CornerTurn(capsule, 90);

			if (k != baseEdge)
			{
				capsule.len = width;
				PlaceEdge(edges, capsule, baseEdgeStyle, "0");
				CornerTurn(capsule, 90);
			}
			else
			{
				PushCapsule(capsule);
				capsule.len = width;
				PlaceEdge(edges, capsule, SCORE, "0");
				PopCapsule(capsule);
			
				CornerTurn(capsule, -360/sides);
				for (var kk = 0; kk < sides - 1; kk++)
				{
					capsule.len = width;
					PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
					Turn(capsule, 360/sides);
				}
				Turn(capsule, -90);
			}

			capsule.len = baseHeight;
			PlaceEdge(edges, capsule, sideMatchStyle, "0");
			CornerTurn(capsule, -(180 - 2*bottomAngle));
		}
	}
	
	// Separate base
	if (this.settings.pyramidBottomStyle == "separate")
	{
		CloseEdge(edges);

		if (!this.settings.alternateLayout)
		{
			// Go back to the starting point and angle
			capsule.pt = {x:startPt.x, y:startPt.y};
			capsule.angle = this.settings.rotate90 ? 90 : 0;
		
			// Move along the first edge (to the "center" or "top" of the pyramid)
			capsule.len = width * sides/2;
			PlaceEdge(edges, capsule, MOVE, "0");
		
			// Move straight down to get below the sides
			Turn(capsule, 90);
			capsule.len = baseHeight;
			// Leave room for a tab and a slot, plus a little extra
			capsule.len += (separateBaseOffset + 10);
			PlaceEdge(edges, capsule, MOVE, "0");
		}
		else
		{
			// Go back to the starting point and angle
			capsule.pt = {x:startPt.x, y:startPt.y};
			capsule.angle = a;
		
			// Move along the first edge (to the "center" or "top" of the pyramid)
			//capsule.len = triangleEdgeLen;
			//PlaceEdge(edges, capsule, MOVE, "0");
		
			// Move straight down to get below the pyramid
			capsule.angle = this.settings.rotate90 ? 0 : 90;
			// For even number of sides, use the edge length, otherwise use the height
			capsule.len = (sides % 2 == 0) ? triangleEdgeLen : topHeight;
			capsule.len += baseHeight;
			// Leave room for a tab and a slot, plus a little extra
			capsule.len += (separateBaseOffset + 10);
			PlaceEdge(edges, capsule, MOVE, "0");
		}
		
		// Move over to center the base
		Turn(capsule, 90);
		capsule.len = width/2;
		PlaceEdge(edges, capsule, MOVE, "0");
		Turn(capsule, 180);

		capsule.len = width;
		for (var kk = 0; kk < sides; kk++)
		{
			PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
			Turn(capsule, 360/sides);
		}
	}
	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Pyramid Fold
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.prototype.GeneratePyramidFold = function()
{
	// Edge list
	var edges = [];
	
	// Settings
	var width  = this.settings.width;

	var topHeight = this.settings.depth;
	var triangleEdgeLen = Math.sqrt(width * width/4 + topHeight * topHeight);
	
	var bottomAngle = Math.atan((topHeight*2)/width) * 180.0 / Math.PI;
	var topAngle = 180 - 2 * bottomAngle;
	var sides = this.settings.sides;
	
	if (sides * topAngle >= 360)
	{
		sides = Math.floor(360/topAngle);
		this.generateWarningStr = "Triangle is too short or too wide for a pyramid with " + this.settings.sides + " sides.";
	}

	// Start angle
	var sweepAngle = sides * topAngle;
	var splitAngle = 360 - sweepAngle;
	var a = (90 - splitAngle/2);
	a += (this.settings.rotate90 ? -90.0 : 0.0);

	// Start point	
	var offset = {x: 10, y: 10};
	offset.x += (this.settings.rotate90) ? (2 * width) : 0;
	offset.y += (this.settings.rotate90) ? 0 : 0;
	var startPt = {x: offset.x, y: offset.y};

	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: 0};
	var baseCapsule = undefined;
	
	// Base and bottom edges
	var baseEdgeStyle = TAB;
	var bottomEdgeStyle = SLOT;
	var separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	var separateBase = (this.settings.pyramidBottomStyle != "attached");
	
	if (this.settings.pyramidBaseEdges == "tabsAndSlots")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = SLOT;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "slotsAndTabs")
	{
		baseEdgeStyle = SLOT;
		bottomEdgeStyle = TAB;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "tabsOnly")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.tabDepth;
	}
	else if (this.settings.pyramidBaseEdges == "flaps")
	{
		baseEdgeStyle = FLAP;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.flapDepth;
	}
	else if (this.settings.pyramidBaseEdges == "none")
	{
		baseEdgeStyle = CUT;
		bottomEdgeStyle = CUT;
		separateBaseOffset = 0;
	}

	// Pyramid edges
	var pyramidEdgeStyle = TAB;
	var pyramidMatchStyle = (this.settings.pyramidTopEdges == "tabAndSlot") ? SLOT : CUT;

	// Start. First open edge of the pyramid
	capsule.len = triangleEdgeLen;
	PlaceEdge(edges, capsule, pyramidEdgeStyle, "0");

	// Add slots between the top triangles of pyramid
	for (var k = 1; k < sides; k++)
	{
		PushCapsule(capsule);
		CornerTurn(capsule, k * topAngle );
		PlaceEdge(edges, capsule, SCORE, "0");
		PopCapsule(capsule);
	}

	// Other open edge of the pyramid
	CornerTurn(capsule, sides * topAngle );
	PlaceEdge(edges, capsule, pyramidMatchStyle, "0");
	
	// Turn to the bottom edge
	CornerTurn(capsule, bottomAngle);
	
	// Bottom edges
	let baseEdge = separateBase ? -1 : Math.floor(sides/2);
	
	for (var k = 0; k < sides; k++)
	{
		if (k != baseEdge)
		{
			capsule.len = width;
			PlaceEdge(edges, capsule, baseEdgeStyle, "0");
			CornerTurn(capsule, 2*bottomAngle);
		}
		else
		{
			PushCapsule(capsule);
			capsule.len = width;
			PlaceEdge(edges, capsule, SCORE, "0");
			PopCapsule(capsule);
			
			CornerTurn(capsule, -360/sides);
			for (var kk = 0; kk < sides - 1; kk++)
			{
				PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
				Turn(capsule, 360/sides);
			}
			Turn(capsule, -2*bottomAngle);
		}
	}
	
	// Separate base
	if (this.settings.pyramidBottomStyle == "separate")
	{
		CloseEdge(edges);

		// Go back to the starting point and angle
		capsule.pt = {x:startPt.x, y:startPt.y};
		capsule.angle = a;
		
		// Move along the first edge (to the "center" or "top" of the pyramid)
		capsule.len = triangleEdgeLen;
		PlaceEdge(edges, capsule, MOVE, "0");
		
		// Move straight down to get below the pyramid
		capsule.angle = this.settings.rotate90 ? 0 : 90;
		// For even number of sides, use the edge length, otherwise use the height
		capsule.len = (sides % 2 == 0) ? triangleEdgeLen : topHeight;
		// Leave room for a tab and a slot, plus a little extra
		capsule.len += (separateBaseOffset + 10);
		PlaceEdge(edges, capsule, MOVE, "0");
		
		// Move over to center the base
		Turn(capsule, 90);
		capsule.len = width/2;
		PlaceEdge(edges, capsule, MOVE, "0");
		Turn(capsule, 180);

		// Create the base
		capsule.len = width;
		for (var kk = 0; kk < sides; kk++)
		{
			PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
			Turn(capsule, 360/sides);
		}
	}
	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Polygon Box Fold
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.prototype.GeneratePolygonBoxFold = function()
{
	// Edge list
	var edges = [];
	
	// Settings
	var width  = this.settings.width;
	var baseHeight = this.settings.height;

	var sides = this.settings.sides;
	
	// Start angle
	var a = 0;
	
	a = this.settings.rotate90 ? 90.0 : 0.0;
	
	// Start point	
	var startPt;
	
	var offset = {x: 10, y: 10};
	offset.x += (this.settings.rotate90) ? (2 * width) : 0;
	offset.y += (this.settings.rotate90) ? 0 : (0);
	startPt = {x: offset.x, y: offset.y};
	
	// Capsule for tracking location and direction and side length
	var capsule = {pt: {x:startPt.x, y:startPt.y}, angle: a, len: 0};
	
	// Base and bottom edges
	var baseEdgeStyle = TAB;
	var bottomEdgeStyle = SLOT;
	var separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	var separateBase = (this.settings.pyramidBottomStyle != "attached");
	
	if (this.settings.pyramidBaseEdges == "tabsAndSlots")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = SLOT;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "slotsAndTabs")
	{
		baseEdgeStyle = SLOT;
		bottomEdgeStyle = TAB;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "tabsOnly")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.tabDepth;
	}
	else if (this.settings.pyramidBaseEdges == "flaps")
	{
		baseEdgeStyle = FLAP;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.flapDepth;
	}
	else if (this.settings.pyramidBaseEdges == "none")
	{
		baseEdgeStyle = CUT;
		bottomEdgeStyle = CUT;
		separateBaseOffset = 0;
	}
	
	// Pyramid edges
	var pyramidEdgeStyle = TAB;
	var pyramidMatchStyle = (this.settings.pyramidTopEdges == "tabAndSlot") ? SLOT : CUT;

	// Base and bottom edges
	var sideEdgeStyle = TAB;
	var sideMatchStyle = (this.settings.pyramidSideEdges == "tabAndSlot") ? SLOT : CUT;;
	
	// Bottom edges
	let topEdge = Math.floor(sides/2 - 0.1);
	let baseEdge = separateBase ? -1 : Math.floor(sides/2);


	for (var k = 0; k < sides; k++)
	{
		if (k != topEdge)
		{
			capsule.len = width;
			PlaceEdge(edges, capsule, baseEdgeStyle, "0");
		}
		else
		{
			PushCapsule(capsule);
			capsule.len = width;
			PlaceEdge(edges, capsule, SCORE, "0");
			PopCapsule(capsule);
		
			CornerTurn(capsule, -360/sides);
			for (var kk = 0; kk < sides - 1; kk++)
			{
				PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
				Turn(capsule, 360/sides);
			}
			Turn(capsule, 180);

		}
	}

	// Vertical edge
	Turn(capsule, 90);
	capsule.len = baseHeight;
	PlaceEdge(edges, capsule, TAB, "0");

	Turn(capsule, 90);
	for (var k = 0; k < sides; k++)
	{
		if (k != baseEdge)
		{
			capsule.len = width;
			PlaceEdge(edges, capsule, baseEdgeStyle, "0");
		}
		else
		{
			PushCapsule(capsule);
			capsule.len = width;
			PlaceEdge(edges, capsule, SCORE, "0");
			PopCapsule(capsule);
		
			CornerTurn(capsule, -360/sides);
			for (var kk = 0; kk < sides - 1; kk++)
			{
				PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
				Turn(capsule, 360/sides);
			}
			Turn(capsule, 180);

		}

		if (k < sides -1)
		{
			PushCapsule(capsule);
			Turn(capsule, 90);
			capsule.len = baseHeight;
			PlaceEdge(edges, capsule, SCORE, "0");
			PopCapsule(capsule);
		}
	}

	Turn(capsule, 90);
	capsule.len = baseHeight;
	PlaceEdge(edges, capsule, SLOT, "0");
	
	// Separate base
	if (this.settings.pyramidBottomStyle == "separate")
	{
		CloseEdge(edges);

		// Go back to the starting point and angle
		capsule.pt = {x:startPt.x, y:startPt.y};
		capsule.angle = this.settings.rotate90 ? 90 : 0;
	
		// Move along the first edge (to the "center" or "top" of the pyramid)
		capsule.len = width * sides/2;
		PlaceEdge(edges, capsule, MOVE, "0");
	
		// Move straight down to get below the sides
		Turn(capsule, 90);
		capsule.len = baseHeight;
		// Leave room for a tab and a slot, plus a little extra
		capsule.len += (separateBaseOffset + 10);
		PlaceEdge(edges, capsule, MOVE, "0");
		
		// Move over to center the base
		Turn(capsule, 90);
		capsule.len = width/2;
		PlaceEdge(edges, capsule, MOVE, "0");
		Turn(capsule, 180);

		capsule.len = width;
		for (var kk = 0; kk < sides; kk++)
		{
			PlaceEdge(edges, capsule, bottomEdgeStyle, "0");
			Turn(capsule, 360/sides);
		}
	}
	
	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Box Fold
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.prototype.Generate = function()
{
	this.EdgeList = [];
	this.generateWarningStr = undefined;


	// Insure that we only create something if we have reasonable settings
	if (this.settings.width >= 0.1 && this.settings.height >= 0.1 && this.settings.depth >= 0.1)
	{
		if (this.settings.shape == "box")
			this.EdgeList = this.GenerateRectangularBoxFold();
		else if (this.settings.shape == "pyrabox")
			this.EdgeList = this.GeneratePyramidBoxFold();
		else if (this.settings.shape == "pyramid")
			this.EdgeList = this.GeneratePyramidFold();
		else if (this.settings.shape == "polybox")
			this.EdgeList = this.GeneratePolygonBoxFold();
	}

	// Keep the flap from extending outside the box for a basic box only
	let flapDepth = this.settings.flapDepth;
	if (this.settings.shape == "box" && this.settings.flapDepth > this.settings.width)
		this.settings.flapDepth = this.settings.width;

	this.gfxDrawing = this.GenerateGfxDrawingFromEdgeList(this.EdgeList, this.settings);

	// Restore flapDepth
	this.settings.flapDepth = flapDepth;
}

//-------------------------------------------------------------------------------------
//	Get Status
//-------------------------------------------------------------------------------------
FoldableBoxDesignerGfxApp.prototype.GetStatus = function()
{
	var status = undefined;
	
	if (this.generateWarningStr != undefined)
		status = { warning:this.generateWarningStr };
		
	return status;
}


//-------------------------------------------------------------------------------------
//	Public
//-------------------------------------------------------------------------------------
export { FoldableBoxDesignerGfxApp };

//-------------------------------------------------------------------------------------
//
//	Foldable Complex Box Designer
//
//		Create plans for folding boxes
//
//-------------------------------------------------------------------------------------


//-------------------------------------------------------------------------------------
//	Constructor
//-------------------------------------------------------------------------------------
function FoldableComplexBoxDesignerGfxApp()
{
	FoldableDesignerGfxApp.call(this, 
		"Foldable Heart Designer", 
		"FoldableComplexBoxDesignerGfxAppId",
		FoldableComplexBoxDesignerGfxApp.defaultSettings, 
		FoldableComplexBoxDesignerGfxApp.controlGroups);
}

FoldableComplexBoxDesignerGfxApp.prototype = Object.create(FoldableDesignerGfxApp.prototype);

Object.defineProperty(FoldableComplexBoxDesignerGfxApp.prototype, 'constructor', {
	value: FoldableComplexBoxDesignerGfxApp,
	enumerable: false,
	write: true });

//-------------------------------------------------------------------------------------
//	Default settings (in addition to the base class default settings)
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.defaultSettings =
{
	shape: "heart",
	
	tabInset: 5,
	tabDepth: 5,
	tabValue: 4,
	slotInset: 0.5,
	slotDepth: 4.0,

	insideAngle: 90.0,
	sideLen: 100,
	insideScale: 1.5,
	topEdges: 5,
	height: 25,
	startStraight: 1,
	separateSides: false,
	includeSlots: false,
	multipleSlots: 1,
	
	addEdgeOutline: false,
	addEdgeExtras: false,
	edgeOutlineInset: 5,
	
	addHeartOutline: false,
	addHeartExtras: false,
	heartOutlineInset: 5,
	
	includeTop: true,
	topEdgeStyle: "none",
	frontEdgeStyle: "tabOnly",
	bottomEdgeStyle: "flaps",
	width: 60,
	depth: 40,
	rotation: 0,
	sides: 5,
	separateBase: false,
	pyramidBaseEdges: "tabsAndSlots",
	pyramidTopEdges: "tabAndSlot",
	pyramidSideEdges: "tabAndSlot",
	pyramidBottomStyle: "attached"
};

//-------------------------------------------------------------------------------------
//	Control Menus
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.sideEdgeSelection = 
	[
		{ label: "Plain",			value: "none" },
		{ label: "Tabs",			value: "tabs" },
		{ label: "Slots",			value: "slots" },
		{ label: "Flaps",			value: "flaps" }
	];

FoldableComplexBoxDesignerGfxApp.bottomStyleSelection =
	[
		{ label: "Attached",		value: "attached" },
		{ label: "Separate",		value: "separate" },
		{ label: "None",			value: "none" }
	];

FoldableComplexBoxDesignerGfxApp.frontEdgeSelection = 
	[
		{ label: "Tab and Slot",	value: "tabAndSlot" },
		{ label: "Tab only",		value: "tabOnly" }
	];

FoldableComplexBoxDesignerGfxApp.baseEdgeSelection = 
	[
		{ label: "Plain",			value: "none" },
		{ label: "Tabs and Slots",	value: "tabsAndSlots" },
		{ label: "Tabs only",		value: "tabsOnly" },
		{ label: "Slots and Tabs",	value: "slotsAndTabs" },
		{ label: "Flaps",			value: "flaps" }
	];

//-------------------------------------------------------------------------------------
//	Controls and Control Groups
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.generalControls =
	[
		{ label: "Angle",			type: "number",		prop: "insideAngle",	min:30, max: 120, step:1, entry:"yes"	},
		{ label: "Size",			type: "number",		prop: "sideLen",		min:5, max: 200, step:0.1, entry:"yes"	},
		{ label: "Adjust",			type: "number",		prop: "insideScale",	min:0.5, max: 2.0, step:0.01, entry:"yes"	},
		{ label: "Edges",			type: "number",		prop: "topEdges",		min:1, max: 12,	   step:1,   entry:"yes"	},
		{ label: "Height",			type: "number",		prop: "height",			min:1, max: 100,  step:1,   entry:"yes"	},
		{ label: "Straight start",	type: "number",		prop: "startStraight",	min:0, max: 12,  step:1,   entry:"yes"	},
		{ label: "Separate sides",	type: "boolean",	prop: "separateSides"		},
		{ label: "Add Slots",		type: "boolean",	prop: "includeSlots"		},
		{ label: "Multiple tabs",	type: "number",		prop: "multipleSlots",	min:1, max: 5,  step:1	},
	];

FoldableComplexBoxDesignerGfxApp.extraControls =
	[
		{ label: "Include edge outline",						type: "boolean",	prop: "addEdgeOutline"		},
		{ label: "&nbsp;&nbsp;&nbsp;Include extra outlines",	type: "boolean",	prop: "addEdgeExtras"		},
		{ label: "&nbsp;&nbsp;&nbsp;Inset",						type: "number",		prop: "edgeOutlineInset",	min:0, max: 10, step:0.1, entry:"yes"	},
		
		{ label: "Include curved heart",	type: "boolean",	prop: "addHeartOutline"			},
		{ label: "&nbsp;&nbsp;&nbsp;Inset",	type: "number",		prop: "heartOutlineInset",		min:0, max: 10, step:0.1, entry:"yes"	}
	];

FoldableComplexBoxDesignerGfxApp.topBottomControls =
	[
		{ label: "Include Top",		type: "boolean",	prop: "includeTop"	},
		{ label: "Top Sides", 		type: "selection",	prop: "topEdgeStyle",		options: FoldableComplexBoxDesignerGfxApp.sideEdgeSelection	},
		{ label: "Front Style", 	type: "selection",	prop: "frontEdgeStyle",		options: FoldableComplexBoxDesignerGfxApp.frontEdgeSelection	},
		{ label: "Bottom Sides", 	type: "selection",	prop: "bottomEdgeStyle",	options: FoldableComplexBoxDesignerGfxApp.sideEdgeSelection	}
	];

FoldableComplexBoxDesignerGfxApp.pyramidControls =
	[
		{ label: "Top Edges", 		type: "selection",	prop: "pyramidTopEdges",		options: FoldableComplexBoxDesignerGfxApp.frontEdgeSelection	},
		{ label: "Side Edges", 		type: "selection",	prop: "pyramidSideEdges",		options: FoldableComplexBoxDesignerGfxApp.frontEdgeSelection	},
		{ label: "Base Edges", 		type: "selection",	prop: "pyramidBaseEdges",		options: FoldableComplexBoxDesignerGfxApp.baseEdgeSelection	},
		{ label: "Bottom",			type: "selection",	prop: "pyramidBottomStyle",		options: FoldableComplexBoxDesignerGfxApp.bottomStyleSelection	},
		{ label: "Alternate Layout",type: "boolean",	prop: "alternateLayout"	}
	];

FoldableComplexBoxDesignerGfxApp.controlGroups = 
	[
		{ label: "General",				controls: FoldableComplexBoxDesignerGfxApp.generalControls	},
		{ label: "Extras",				controls: FoldableComplexBoxDesignerGfxApp.extraControls	},
	//	{ label: "Basic Box",			controls: FoldableComplexBoxDesignerGfxApp.topBottomControls	},
		{ label: "Tab Settings",		controls: FoldableDesignerGfxApp.tabControls		},
		{ label: "Slot Settings",		controls: FoldableDesignerGfxApp.slotControls		},
		{ label: "Score Settings",		controls: FoldableDesignerGfxApp.scoreControls		},
	];


//-------------------------------------------------------------------------------------
//	Simple (hard-coded) Heart Box Points 
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.prototype.SimpleHeartBoxPoints = function()
{
	let sp = {x:100, y:300};
	var shapePts = [];
	shapePts.push({x:sp.x + 0, y:sp.y + 0, render:1});
	shapePts.push({x:sp.x + -80, y:sp.y + -80, render:1});
	shapePts.push({x:sp.x + -80, y:sp.y + -100, render:3});
	shapePts.push({x:sp.x + -60, y:sp.y + -120, render:0});
	shapePts.push({x:sp.x + -20, y:sp.y + -120, render:0});
	shapePts.push({x:sp.x + 0, y:sp.y + -100, render:0});
	shapePts.push({x:sp.x + 20, y:sp.y + -120, render:0});
	shapePts.push({x:sp.x + 60, y:sp.y + -120, render:-3});
	shapePts.push({x:sp.x + 80, y:sp.y + -100});
	shapePts.push({x:sp.x + 80, y:sp.y + -80});

	return shapePts;
}

//-------------------------------------------------------------------------------------
//	Calc Heart Box Points 
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.prototype.CalcHeartBoxPoints = function()
{
	var heart = {};
	let sp = {x:100, y:300};
	var shapePts = [];// = this.SimpleHeartBoxPoints();
	
	// Heart Description
	var insideAngle   = this.settings.insideAngle;
	var sideLen       = this.settings.sideLen;
	var insideScale   = this.settings.insideScale;
	var topEdges      = this.settings.topEdges;
	var startStraight = this.settings.startStraight;
	
	// 
	var angle = (90.0 - insideAngle/2.0) * Math.PI / 180.0;
	var startPt = {x: 0, y: 0};
	var edgePt = {x:sideLen * Math.cos(angle), y:sideLen * Math.sin(angle)};
	var topCtrPt = {x:0, y:edgePt.y * insideScale};
	var arcCtr = MathUtil.CalcPointBetween(edgePt, topCtrPt, 0.5);
	var startAngle = MathUtil.CalcAngle(arcCtr, edgePt);
	var radius = MathUtil.DistanceBetween(arcCtr, edgePt);
	var arcPts = [];
	
	// Calculate the points for the arc
	for (var kk = 0; kk < topEdges; kk++)
	{
		var a = startAngle + kk * Math.PI/topEdges;
		var p = {x:arcCtr.x + radius * Math.cos(a), y:arcCtr.y + radius * Math.sin(a)};
		if (kk == startStraight)
			p.render = topEdges - startStraight;
		else if (kk > startStraight)
			p.render = 0;
			
		arcPts.push(p);
	}


	// Origin
	var origin = {x:0, y:0};
	
	// Func to invert vertical, mirror points, translate points, fix-up 'render' value
	function fixPt(p, mirror = false) { 
		let fixed = { x:origin.x + p.x * (mirror?-1:1), y:origin.y - p.y };
		
		if (p.render != undefined)
		{
			if (p.render == 0)
				fixed.render = p.render;
			else
				fixed.render = (mirror) ? p.render : -p.render;
		}
		
		return fixed;
	}
	
	// Build the point list
	shapePts.push(fixPt(startPt));
	
	for (var kk = 0; kk < arcPts.length; kk++)
		shapePts.push(fixPt(arcPts[kk], true));
		
	let mid = shapePts.length;
	shapePts.push(fixPt(topCtrPt));
	
	for (var kk = arcPts.length - 1; kk >= 0; kk--)
		shapePts.push(fixPt(arcPts[kk]));
	
	// Shift the "render" values for the second half, since they should be on the 
	// first point of the edge
	for (var kk = mid; kk < shapePts.length - 1; kk++)
	{
		if (shapePts[kk + 1].render != undefined)
		{
			shapePts[kk].render = shapePts[kk + 1].render;
			shapePts[kk + 1].render = undefined;
		}
	}
	
	// Create edge polygon list
	var edgePolyList = undefined;
	if (this.settings.addEdgeOutline)
	{
		var points = [];
		for (var kk = 0; kk < shapePts.length; kk++)
			points.push({x:shapePts[kk].x, y:shapePts[kk].y});
	
		edgePolyList = new PolygonList();
		edgePolyList.AddPolygonPoints(points);
	}
	
	// Curved heart
	let curvedHeartGfxPath = undefined;
	if (this.settings.addHeartOutline)
	{
		let curvedInset = this.settings.heartOutlineInset;
		let curvedVertShift = curvedInset / Math.sin(insideAngle/2 * Math.PI/180.0);
		let curvedLenAdj = curvedInset / Math.tan(insideAngle/2 * Math.PI/180.0);
		let curvedSideLine = sideLen - 2 * curvedInset;
		let curvedStartPt = {x:startPt.x, y:startPt.y - curvedVertShift};
		var curvedEdgePt = {x:curvedStartPt.x + curvedSideLine * Math.cos(angle), y:curvedStartPt.y - curvedSideLine * Math.sin(angle)};
		var curvedTopCtrPt = {x:0, y:curvedStartPt.y + (curvedEdgePt.y - curvedStartPt.y)* insideScale};
		var curvedArcCtr = MathUtil.CalcPointBetween(curvedEdgePt, curvedTopCtrPt, 0.5);
		var curvedStartAngle = MathUtil.CalcAngle(curvedArcCtr, curvedEdgePt);
		var curvedRadius = MathUtil.DistanceBetween(curvedArcCtr, curvedEdgePt);
		
		curvedHeartGfxPath = new GfxPath();
		curvedHeartGfxPath.MoveTo(curvedStartPt);
		curvedHeartGfxPath.LineTo(curvedEdgePt);
		curvedHeartGfxPath.Arc(curvedArcCtr, curvedRadius, curvedStartAngle, curvedStartAngle + Math.PI, 1);
		curvedHeartGfxPath.LineTo(curvedTopCtrPt);
		curvedHeartGfxPath.Arc({x:-curvedArcCtr.x, y:curvedArcCtr.y}, curvedRadius, -curvedStartAngle, Math.PI - curvedStartAngle, 1);
		curvedHeartGfxPath.Close();
		
	}
	// Return data
	heart.points = shapePts;
	heart.edgePolyList = edgePolyList;
	heart.curvedHeartGfxPath = curvedHeartGfxPath;

	return heart;
}

//-------------------------------------------------------------------------------------
//	Polygon List to GfxPath
//-------------------------------------------------------------------------------------
function PolygonListToGfxPath(polyList, inset)
{
	var gfxPath = new GfxPath();
	
	var pl = (inset > epsilon) ? polyList.CalcOffsetPolygon(-inset) : polyList;
	
	for (var jj = 0; jj < pl.GetPolygonCount(); jj++)
	{
		var p = pl.GetPolygonPoints(jj);
		
		gfxPath.MoveTo(p[0]);
		for (var kk = 1; kk < p.length; kk++)
			gfxPath.LineTo(p[kk]);
		gfxPath.Close();
	}
	
	return gfxPath;
}


//-------------------------------------------------------------------------------------
//	Generate Heart Box Fold
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.prototype.GenerateHeartBoxFold = function()
{
	// Edge list
	var edges = [];
	
	// Settings
	let height = this.settings.height;
	var separateSides = this.settings.separateSides;
	var includeSlots = this.settings.includeSlots;
	var multipleSlots = this.settings.multipleSlots;
	
	// Base and bottom edges
	var baseEdgeStyle = TAB;
	var bottomEdgeStyle = SLOT;
	var separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	var separateBase = (this.settings.pyramidBottomStyle != "attached");
	
	if (this.settings.pyramidBaseEdges == "tabsAndSlots")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = SLOT;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "slotsAndTabs")
	{
		baseEdgeStyle = SLOT;
		bottomEdgeStyle = TAB;
		separateBaseOffset = this.settings.tabDepth + this.settings.slotDepth;
	}
	else if (this.settings.pyramidBaseEdges == "tabsOnly")
	{
		baseEdgeStyle = TAB;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.tabDepth;
	}
	else if (this.settings.pyramidBaseEdges == "flaps")
	{
		baseEdgeStyle = FLAP;
		bottomEdgeStyle = CUT;
		separateBaseOffset = this.settings.flapDepth;
	}
	else if (this.settings.pyramidBaseEdges == "none")
	{
		baseEdgeStyle = CUT;
		bottomEdgeStyle = CUT;
		separateBaseOffset = 0;
	}
	
	// Pyramid edges
	var pyramidEdgeStyle = TAB;
	var pyramidMatchStyle = (this.settings.pyramidTopEdges == "tabAndSlot") ? SLOT : CUT;

	// Base and bottom edges
	var sideEdgeStyle = TAB;
	var sideMatchStyle = (this.settings.pyramidSideEdges == "tabAndSlot") ? SLOT : CUT;;
	
	// List of points for the shape
	var heart = this.CalcHeartBoxPoints();
	var shapePts = heart.points;
	
	
	let slotOrCut = includeSlots ? SLOT : CUT;

	let edgeCount = shapePts.length;
	
	if (separateSides)
	{
		// Side strip is separate from heart
		
		// Edges for the heart
		for (var k = 0; k < edgeCount; k++)
		{
			let edgeConfig = (k == 0 || k == edgeCount - 1) ? {subdivide:multipleSlots} : undefined;
			AddEdge(edges, shapePts[k], shapePts[(k+1) % edgeCount], TAB, "0", edgeConfig);
		}
		CloseEdge(edges);

		// Find the maximum x value
		let maxX = shapePts[0].x;
		for (var kk = 0; kk < shapePts.length; kk++)
			if (maxX < shapePts[kk].x)
				maxX = shapePts[kk].x;
				
		for (var jj = 0; jj < 2; jj ++)
		{
			// Position the side strips just to the left and right of the heart
			let horzPosition = maxX + this.settings.tabDepth + this.settings.slotDepth + 1;
			
			if (jj == 0)
				horzPosition += height;
			else
				horzPosition *= -1;
			
			var ptA = {x: horzPosition, y: 0};
	
			var uv = {x:0, y:-1};
			var up = {x:uv.y, y:-uv.x};
	
			var p = PointVectorAdd(ptA, up, height);
			AddEdge(edges, ptA, p, (jj == 0) ? TAB : slotOrCut, "0");
	
			// In order to get the sides to be symmetrical, on the first pass we count up, 
			// and on the second pass we count down
			let startEdge;
			let endEdge;
			if (jj == 0)
			{
				startEdge = 0;
				endEdge = edgeCount/2 - 1;
			}
			else
			{
				startEdge = edgeCount - 1;
				endEdge = edgeCount/2;
			}
			
			// Set the direction for the for-loops based on counting up or down
			let dir = (startEdge < endEdge) ? 1 : -1;
			
			// Make the CUTs and SLOTs symmetrical
			let edgeTypeA = (jj == 0) ? slotOrCut : CUT;
			let edgeTypeB = (jj == 0) ? CUT : slotOrCut;
			
			// The edges on one side, plus the score lines
			for (var kk = startEdge; kk != (endEdge + dir); kk += dir)
			{
				if (kk != startEdge)
				{
					let ps = PointVectorAdd(p, up, -height);
					AddEdge(edges, p, ps, SCORE, "0");
				}
							
				let ptC = shapePts[(kk) % edgeCount]
				let ptD = shapePts[(kk+1) % edgeCount]
				let l2 = MathUtil.DistanceBetween(ptC, ptD);
				var p2 = p;
				p = PointVectorAdd(p, uv, l2);
				let edgeConfig = (kk == 0 || kk == edgeCount - 1) ? {subdivide:multipleSlots} : undefined;
				AddEdge(edges, p2, p, edgeTypeA, "0", edgeConfig);
			}
		
			var p2 = p;
			p = PointVectorAdd(p, up, -height);
			AddEdge(edges, p2, p,  (jj == 0) ? slotOrCut : TAB, "0");
		
			// The edges on the other side
			for (var kk = endEdge; kk != (startEdge - dir); kk -= dir)
			{
				let ptC = shapePts[(kk) % edgeCount]
				let ptD = shapePts[(kk+1) % edgeCount]
				let l2 = MathUtil.DistanceBetween(ptC, ptD);
				var p2 = p;
				p = PointVectorAdd(p, uv, -l2);
				let edgeConfig = (kk == 0 || kk == edgeCount - 1) ? {subdivide:multipleSlots} : undefined;
				AddEdge(edges, p2, p, edgeTypeB, "0", edgeConfig);
			}
			
			CloseEdge(edges);

		}
	}
	else 
	{
		// Side strip is attached to heart
		//
		for (var k = 0; k < edgeCount; k++)
		{
			if (shapePts[k].render == undefined || shapePts[k].render == 1)
			{
				AddEdge(edges, shapePts[k], shapePts[(k+1) % edgeCount], SCORE, "0");
		
				var ptA = shapePts[k];
				var ptB = shapePts[(k+1) % edgeCount];
		
				var uv = MathUtil.CalcUnitVector(ptA, ptB);
				var up = {x:uv.y, y:-uv.x};
				var len = MathUtil.DistanceBetween(ptA, ptB);
		
				var p = PointVectorAdd(ptA, up, height);
				AddEdge(edges, ptA, p, TAB, "0");
		
				var p2 = p;
				p = PointVectorAdd(p, uv, len);
				AddEdge(edges, p2, p, CUT, "0");
		
				var p2 = p;
				p = PointVectorAdd(p, up, -height);
				AddEdge(edges, p2, p, slotOrCut, "0");
			}
			else if (shapePts[k].render == 0)
			{
				AddEdge(edges, shapePts[k], shapePts[(k+1) % edgeCount], TAB, "0");
			}
			else if (shapePts[k].render > 1)
			{
				let r = shapePts[k].render;
			
				AddEdge(edges, shapePts[k], shapePts[(k+1) % edgeCount], SCORE, "0");
		
				var ptA = shapePts[k];
				var ptB = shapePts[(k+1) % edgeCount];
		
				var uv = MathUtil.CalcUnitVector(ptA, ptB);
				var up = {x:uv.y, y:-uv.x};
				var len = MathUtil.DistanceBetween(ptA, ptB);
		
				var p = PointVectorAdd(ptA, up, height);
				AddEdge(edges, ptA, p, TAB, "0");
		
				var p2 = p;
				p = PointVectorAdd(p, uv, len);
				AddEdge(edges, p2, p, CUT, "0");

				for (var kk = 1; kk < r; kk++)
				{
					let ps = PointVectorAdd(p, up, -height);
					AddEdge(edges, p, ps, SCORE, "0");
				
					let ptC = shapePts[(k+kk) % edgeCount]
					let ptD = shapePts[(k+kk+1) % edgeCount]
					let l2 = MathUtil.DistanceBetween(ptC, ptD);
					var p2 = p;
					p = PointVectorAdd(p, uv, l2);
					AddEdge(edges, p2, p, CUT, "0");
				}
			
				var p2 = p;
				p = PointVectorAdd(p, up, -height);
				AddEdge(edges, p2, p, slotOrCut, "0");
			
		
				for (var kk = r -1; kk >= 1; kk--)
				{
					let ptC = shapePts[(k+kk) % edgeCount]
					let ptD = shapePts[(k+kk+1) % edgeCount]
					let l2 = MathUtil.DistanceBetween(ptC, ptD);
					var p2 = p;
					p = PointVectorAdd(p, uv, -l2);
					AddEdge(edges, p2, p, slotOrCut, "0");
				}
			
			
			}
			else if (shapePts[k].render < 0)
			{
				let r = -shapePts[k].render;
			
				AddEdge(edges, shapePts[k], shapePts[(k+1) % edgeCount], SCORE, "0");
		
				var ptA = shapePts[k];
				var ptB = shapePts[(k+1) % edgeCount];
		
				var uv = MathUtil.CalcUnitVector(ptA, ptB);
				var up = {x:uv.y, y:-uv.x};
				var len = MathUtil.DistanceBetween(ptA, ptB);
		
				var p = {x:ptA.x, y:ptA.y};
		

				for (var kk = 1; kk < r; kk++)
				{
					let ps = PointVectorAdd(p, up, height);
					AddEdge(edges, p, ps, SCORE, "0");
				
					let ptC = shapePts[(k-kk) % edgeCount]
					let ptD = shapePts[(k-kk+1) % edgeCount]
					let l2 = MathUtil.DistanceBetween(ptC, ptD);
					var p2 = p;
					p = PointVectorAdd(p, uv, -l2);
					AddEdge(edges, p2, p, slotOrCut, "0");
				}
			
				var p2 = p;
				p = PointVectorAdd(p, up, height);
				AddEdge(edges, p2, p, TAB, "0");
			
		
				for (var kk = r -1; kk >= 1; kk--)
				{
					let ptC = shapePts[(k-kk) % edgeCount]
					let ptD = shapePts[(k-kk+1) % edgeCount]
					let l2 = MathUtil.DistanceBetween(ptC, ptD);
					var p2 = p;
					p = PointVectorAdd(p, uv, l2);
					AddEdge(edges, p2, p, CUT, "0");
				}
			
				var p2 = p;
				p = PointVectorAdd(p, uv, len);
				AddEdge(edges, p2, p, CUT, "0");
			
				var p2 = p;
				var p = PointVectorAdd(p2, up, -height);
				AddEdge(edges, p2, p, slotOrCut, "0");
			}

		} 
	
	}
	
	// Find minimum values
	function minPt(ptA, ptB) { return {x:(ptA.x < ptB.x) ? ptA.x : ptB.x, y:(ptA.y < ptB.y) ? ptA.y : ptB.y } }
	
	let min = minPt(edges[0].ptA, edges[0].ptB);
	for (var kk = 1; kk < edges.length; kk++)
		min = minPt(min, minPt(edges[kk].ptA, edges[kk].ptB));
		
	//edges.forEach(e => {e.ptA.x -= min.x; e.ptA.y -= min.y; e.ptB.x -= min.x; e.ptB.y -= min.y});
	
	
	this.edgeOffsetGfxPath = undefined;
	this.edgeBorderGfxPath = undefined;
	this.edgeClipGfxPath = undefined;
	
	if (this.settings.addEdgeOutline && heart.edgePolyList != undefined)
	{
		let edgeOutlineInset = this.settings.edgeOutlineInset;

		// Translate the edgePolyList to match the edges
		//heart.edgePolyList.Translate({x:-min.x, y:-min.y});
		
		if (this.settings.addEdgeExtras)
		{
			// Add the "edge outline" as a border area (two polygons) and a single polygon
			//
			
			this.edgeClipGfxPath = PolygonListToGfxPath(heart.edgePolyList, edgeOutlineInset/2);

			// Create polygonList that has the edgeOutline going forward and reverse, so that
			// we can create an area we can fill that represents the border.
			//
			// Start by getting a polygon that is in the middle
			let bp = heart.edgePolyList.CalcOffsetPolygon(-edgeOutlineInset/2)
			let hp = bp.GetPolygonPoints(0);
			// Make two copies, one forward and one reverse
			let hpA = JSON.parse(JSON.stringify(hp));
			let hpB = JSON.parse(JSON.stringify(hp)).reverse();
			// Add both to a new polygon list
			let borderPolyList = new PolygonList();
			borderPolyList.AddPolygonPoints(hpA);
			borderPolyList.AddPolygonPoints(hpB);
			// Create a gfxPath of with an offset, which creates the bordered area
			this.edgeBorderGfxPath = PolygonListToGfxPath(borderPolyList, edgeOutlineInset/2);
		}
		else 
		{
			// Add only the "edge outline"
			this.edgeOffsetGfxPath = PolygonListToGfxPath(heart.edgePolyList, edgeOutlineInset);
		}
	}
	
	this.curvedHeartGfxPath = heart.curvedHeartGfxPath;

	return edges
}

//-------------------------------------------------------------------------------------
//	Generate Box Fold
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.prototype.Generate = function()
{
	this.EdgeList = [];
	this.generateWarningStr = undefined;


	// Insure that we only create something if we have reasonable settings
	if (this.settings.width >= 0.1 && this.settings.height >= 0.1 && this.settings.depth >= 0.1)
	{
		this.EdgeList = this.GenerateHeartBoxFold();
	}

	this.gfxDrawing = this.GenerateGfxDrawingFromEdgeList(this.EdgeList, this.settings);
	
	
	if (this.edgeBorderGfxPath != undefined)
		this.gfxDrawing.AddPath(this.edgeBorderGfxPath, { strokePath:true, strokeColor:"#80ff80", strokeWidth:0.25, fillPath:true, fillColor:"#ccffcc"} );
		
	if (this.edgeOffsetGfxPath != undefined)
		this.gfxDrawing.AddPath(this.edgeOffsetGfxPath, { strokePath:true, strokeColor:"#80ff80", strokeWidth:0.5} );
		
	if (this.edgeClipGfxPath != undefined)
		this.gfxDrawing.AddPath(this.edgeClipGfxPath, { strokePath:true, strokeColor:"#00FF00", strokeWidth:0.25} );
		
	if (this.curvedHeartGfxPath != undefined)
		this.gfxDrawing.AddPath(this.curvedHeartGfxPath, { strokePath:true, strokeColor:"#FF0000", strokeWidth:0.25} );
}

//-------------------------------------------------------------------------------------
//	Get Status
//-------------------------------------------------------------------------------------
FoldableComplexBoxDesignerGfxApp.prototype.GetStatus = function()
{
	var status = undefined;
	
	if (this.generateWarningStr != undefined)
		status = { warning:this.generateWarningStr };
		
	return status;
}


//-------------------------------------------------------------------------------------
//	Public
//-------------------------------------------------------------------------------------
export { FoldableComplexBoxDesignerGfxApp };
